// src/theme/mui-theme.ts
import { createTheme } from "@mui/material/styles";

// src/theme/tokens.ts
var colors = {
  // ── Brand purple (primary) ──────────────────────────────────────────────────
  primary: {
    50: "#f3eeff",
    100: "#e4d4ff",
    200: "#cbaeff",
    300: "#a97ef9",
    400: "#8b55f6",
    500: "#7C3AED",
    // main – Paddiq violet
    600: "#6d28d9",
    700: "#5b21b6",
    800: "#4c1d95",
    900: "#3b1678",
    A100: "#d9b8ff",
    A200: "#b57bff",
    A400: "#8b40ff",
    A700: "#7826ff"
  },
  // ── Secondary – pit-lane amber ─────────────────────────────────────────────
  secondary: {
    50: "#fffbea",
    100: "#fff3c4",
    200: "#ffe88a",
    300: "#ffd84d",
    400: "#ffc72b",
    500: "#FFAB00",
    // main
    600: "#e69900",
    700: "#cc8800",
    800: "#a36e00",
    900: "#7a5200",
    A100: "#ffe680",
    A200: "#ffd740",
    A400: "#ffc400",
    A700: "#ffab00"
  },
  // ── Semantic ────────────────────────────────────────────────────────────────
  error: {
    light: "#f87171",
    main: "#ef4444",
    dark: "#dc2626",
    contrastText: "#ffffff"
  },
  warning: {
    light: "#fb923c",
    main: "#f97316",
    dark: "#ea580c",
    contrastText: "#ffffff"
  },
  info: {
    light: "#38bdf8",
    main: "#0ea5e9",
    dark: "#0284c7",
    contrastText: "#ffffff"
  },
  success: {
    light: "#4ade80",
    main: "#22c55e",
    dark: "#16a34a",
    contrastText: "#ffffff"
  },
  // ── Neutral greys ───────────────────────────────────────────────────────────
  grey: {
    50: "#f9fafb",
    100: "#f3f4f6",
    200: "#e5e7eb",
    300: "#d1d5db",
    400: "#9ca3af",
    500: "#6b7280",
    600: "#4b5563",
    700: "#374151",
    800: "#1f2937",
    900: "#111827"
  },
  // ── Dark theme surfaces ────────────────────────────────────────────────────
  dark: {
    bg: "#0B0B1A",
    sidebar: "#09090B",
    surface: "#13132B",
    elevated: "#1A1A38",
    border: "rgba(255,255,255,0.07)",
    hover: "rgba(255,255,255,0.05)"
  },
  // ── Light theme surfaces ───────────────────────────────────────────────────
  light: {
    bg: "#F5F6FA",
    sidebar: "#FAFAFA",
    surface: "#FFFFFF",
    elevated: "#FFFFFF",
    border: "rgba(0,0,0,0.08)",
    hover: "rgba(0,0,0,0.04)"
  },
  // ── Sidebar chrome (nav, profile, team selector) ─────────────────────────
  sidebarChrome: {
    dark: {
      text: "#F4F4F5",
      textSecondary: "#A1A1AA",
      textMuted: "#71717A",
      border: "rgba(255,255,255,0.08)",
      navText: "#B8B8C0",
      navHover: "rgba(255,255,255,0.05)",
      navActiveBg: "#1C192E",
      navActiveText: "#E7E1FF",
      navActiveIcon: "#A78BFA",
      navActiveHover: "#242039",
      teamCardBg: "transparent",
      profileCardBg: "#111113",
      menuBg: "#111113",
      menuText: "#F4F4F5",
      collapseText: "#A1A1AA",
      profileAvatarBg: "#27272A",
      profileAvatarBorder: "rgba(255,255,255,0.12)",
      teamAvatarBg: "#18181B",
      teamAvatarText: "#D4D4D8",
      teamAvatarBorder: "rgba(255,255,255,0.1)"
    },
    light: {
      text: "#18181B",
      textSecondary: "#71717A",
      textMuted: "#A1A1AA",
      border: "#E4E4E7",
      navText: "#3F3F46",
      navHover: "#F1F1F3",
      navActiveBg: "#EFEDFA",
      navActiveText: "#4C3B88",
      navActiveIcon: "#6D5BD0",
      navActiveHover: "#E8E4F8",
      teamCardBg: "transparent",
      profileCardBg: "#FFFFFF",
      menuBg: "#FFFFFF",
      menuText: "#18181B",
      collapseText: "#71717A",
      profileAvatarBg: "#27272A",
      profileAvatarBorder: "#E4E4E7",
      teamAvatarBg: "#F4F4F5",
      teamAvatarText: "#52525B",
      teamAvatarBorder: "#E4E4E7"
    }
  },
  // ── Page header chrome (titles, toolbar buttons, replay bar, weather) ───────
  headerChrome: {
    dark: {
      sectionLabel: "#94A3B8",
      buttonBorder: "rgba(36,62,82,0.78)",
      buttonText: "#FFFFFF",
      buttonBg: "rgba(1,13,24,0.58)",
      buttonBorderHover: "rgba(80,113,138,0.9)",
      buttonBgHover: "rgba(255,255,255,0.04)",
      buttonSecondary: "#64748B",
      replayBarBorder: "rgba(80,113,138,0.52)",
      replayBarBg: "rgba(4,15,27,0.92)",
      replayBarShadow: "inset 0 1px 0 rgba(255,255,255,0.05), 0 8px 22px rgba(0,0,0,0.22)",
      replayTimeText: "#CBD5E1",
      replayMetaText: "#64748B",
      replayMetaBg: "rgba(255,255,255,0.05)",
      replayMetaBorder: "rgba(255,255,255,0.08)",
      replayMetaLabel: "#D8DEE8",
      replayPlayBorder: "rgba(183,140,255,0.58)",
      replayPlayBg: "rgba(183,140,255,0.24)",
      replayPlayBgActive: "rgba(183,140,255,0.38)",
      replayPlayBgHover: "rgba(183,140,255,0.48)",
      weatherTitle: "#FFFFFF",
      weatherSubtitle: "#A6B0BE",
      weatherHoverBg: "rgba(255,255,255,0.07)",
      weatherDivider: "rgba(36,62,82,0.72)",
      mapDockBorder: "rgba(36,62,82,0.72)",
      mapDockBg: "rgba(2,10,18,0.94)",
      mapDockShadow: "0 12px 32px rgba(0,0,0,0.42)",
      mapDockLegendBg: "rgba(255,255,255,0.03)",
      mapDockLegendText: "#A6B0BE"
    },
    light: {
      sectionLabel: "#6B7280",
      buttonBorder: "#E5E7EB",
      buttonText: "#1F2937",
      buttonBg: "#FFFFFF",
      buttonBorderHover: "#D1D5DB",
      buttonBgHover: "#F9FAFB",
      buttonSecondary: "#6B7280",
      replayBarBorder: "#D1D5DB",
      replayBarBg: "#FFFFFF",
      replayBarShadow: "none",
      replayTimeText: "#374151",
      replayMetaText: "#6B7280",
      replayMetaBg: "#F9FAFB",
      replayMetaBorder: "#E5E7EB",
      replayMetaLabel: "#374151",
      replayPlayBorder: "rgba(124,58,237,0.35)",
      replayPlayBg: "rgba(124,58,237,0.12)",
      replayPlayBgActive: "rgba(124,58,237,0.2)",
      replayPlayBgHover: "rgba(124,58,237,0.18)",
      weatherTitle: "#1F2937",
      weatherSubtitle: "#6B7280",
      weatherHoverBg: "rgba(0,0,0,0.04)",
      weatherDivider: "#E5E7EB",
      mapDockBorder: "#E5E7EB",
      mapDockBg: "rgba(255,255,255,0.98)",
      mapDockShadow: "0 10px 28px rgba(15,23,42,0.12)",
      mapDockLegendBg: "#F9FAFB",
      mapDockLegendText: "#374151"
    }
  },
  // ── Lap meta drawer (bottom sheet on Sessions map) ─────────────────────────
  lapMetaChrome: {
    dark: {
      paperBg: "#010D18",
      border: "rgba(36,62,82,0.82)",
      headerBorder: "rgba(36,62,82,0.72)",
      title: "#F8FAFC",
      subtitle: "#94A3B8",
      toggleText: "#94A3B8",
      toggleBorder: "rgba(36,62,82,0.72)",
      toggleSelectedText: "#FFFFFF",
      toggleSelectedBg: "rgba(168,85,247,0.18)",
      closeBorder: "rgba(36,62,82,0.72)",
      closeText: "#CBD5E1",
      emptyText: "#94A3B8",
      tableBorder: "rgba(36,62,82,0.72)",
      tableBg: "rgba(1,13,24,0.48)",
      tableHeadBg: "#010D18",
      tableHeadText: "#94A3B8",
      tableHeadBorder: "rgba(36,62,82,0.82)",
      rowBorder: "rgba(36,62,82,0.36)",
      lapText: "#F8FAFC",
      kindText: "#C4B5FD",
      monoText: "#E2E8F0",
      utcText: "#CBD5E1",
      mutedText: "#94A3B8"
    },
    light: {
      paperBg: "#FFFFFF",
      border: "#E5E7EB",
      headerBorder: "#E5E7EB",
      title: "#111827",
      subtitle: "#6B7280",
      toggleText: "#6B7280",
      toggleBorder: "#E5E7EB",
      toggleSelectedText: "#7C3AED",
      toggleSelectedBg: "rgba(124,58,237,0.12)",
      closeBorder: "#E5E7EB",
      closeText: "#6B7280",
      emptyText: "#6B7280",
      tableBorder: "#E5E7EB",
      tableBg: "#FFFFFF",
      tableHeadBg: "#F9FAFB",
      tableHeadText: "#6B7280",
      tableHeadBorder: "#E5E7EB",
      rowBorder: "#F3F4F6",
      lapText: "#111827",
      kindText: "#7C3AED",
      monoText: "#374151",
      utcText: "#4B5563",
      mutedText: "#9CA3AF"
    }
  },
  // ── Session & laps drawer (right panel on Sessions map) ────────────────────
  sessionDrawerChrome: {
    dark: {
      paperBg: "#010D18",
      border: "rgba(36,62,82,0.72)",
      headerBorder: "rgba(36,62,82,0.72)",
      title: "#FFFFFF",
      sectionLabel: "#94A3B8",
      body: "#64748B",
      muted: "#64748B",
      empty: "#94A3B8",
      error: "#F87171",
      resetText: "#94A3B8",
      resetBorder: "rgba(36,62,82,0.72)",
      resetHoverBg: "rgba(255,255,255,0.04)",
      closeText: "#94A3B8",
      closeBorder: "rgba(36,62,82,0.72)",
      divider: "rgba(36,62,82,0.72)",
      cardBg: "rgba(1,13,24,0.46)",
      cardBorder: "rgba(36,62,82,0.72)",
      collapsibleTitle: "#CBD5E1",
      collapsibleTitleCollapsed: "#94A3B8",
      collapsibleSummary: "#64748B",
      collapsibleIcon: "#64748B",
      removeHoverText: "#F87171",
      removeHoverBg: "rgba(239,68,68,0.08)",
      addCompareText: "#E2E8F0",
      addCompareDisabledText: "#64748B",
      addCompareBorder: "rgba(36,62,82,0.72)",
      toggleText: "#94A3B8",
      toggleBorder: "rgba(36,62,82,0.72)",
      toggleSelectedText: "#FFFFFF",
      toggleSelectedBg: "rgba(255,255,255,0.06)",
      label: "#94A3B8",
      inputText: "#FFFFFF",
      inputBg: "rgba(1,13,24,0.46)",
      inputBorder: "rgba(36,62,82,0.72)",
      inputBorderHover: "rgba(80,113,138,0.9)",
      menuBg: "#010D18",
      menuBorder: "rgba(36,62,82,0.72)",
      menuShadow: "0 12px 32px rgba(0,0,0,0.45)",
      menuPrimaryText: "#FFFFFF",
      menuSecondaryText: "#94A3B8",
      menuSubheaderBg: "#010D18",
      menuSubheaderText: "#64748B",
      sectionCardBg: "rgba(1,13,24,0.55)",
      sectionCardBorder: "rgba(36,62,82,0.55)",
      tableBg: "rgba(1,13,24,0.46)",
      tableBorder: "rgba(36,62,82,0.72)",
      tableHeadBg: "#010D18",
      tableHeadText: "#94A3B8",
      tableHeadBorder: "rgba(36,62,82,0.72)",
      tableRowBorder: "rgba(36,62,82,0.36)",
      lapTimeText: "#E2E8F0",
      lapGapText: "#94A3B8",
      lapOut: "#FBBF24",
      lapOutSelected: "#FDE68A",
      lapOutSelectedBg: "rgba(245,158,11,0.14)",
      lapIn: "#94A3B8",
      lapInSelected: "#E2E8F0",
      lapInSelectedBg: "rgba(148,163,184,0.12)",
      lapCrash: "#F87171",
      lapCrashSelected: "#FCA5A5",
      lapCrashSelectedBg: "rgba(239,68,68,0.14)",
      lapFlying: "#E2E8F0",
      lapFlyingSelected: "#DDD6FE",
      lapFlyingSelectedBg: "rgba(124,58,237,0.13)",
      lapCompareSelectedBg: "rgba(245,158,11,0.1)",
      lapCompareHoverBg: "rgba(245,158,11,0.06)",
      lapPrimaryHoverBg: "rgba(124,58,237,0.08)",
      footageChipText: "#D1FAE5",
      footageChipBg: "rgba(16,185,129,0.16)",
      footageChipBorder: "rgba(52,211,153,0.35)",
      footageChipIcon: "#6EE7B7"
    },
    light: {
      paperBg: "#FFFFFF",
      border: "#E5E7EB",
      headerBorder: "#E5E7EB",
      title: "#111827",
      sectionLabel: "#6B7280",
      body: "#6B7280",
      muted: "#9CA3AF",
      empty: "#6B7280",
      error: "#DC2626",
      resetText: "#6B7280",
      resetBorder: "#E5E7EB",
      resetHoverBg: "rgba(0,0,0,0.04)",
      closeText: "#6B7280",
      closeBorder: "#E5E7EB",
      divider: "#E5E7EB",
      cardBg: "#F9FAFB",
      cardBorder: "#E5E7EB",
      collapsibleTitle: "#111827",
      collapsibleTitleCollapsed: "#6B7280",
      collapsibleSummary: "#6B7280",
      collapsibleIcon: "#9CA3AF",
      removeHoverText: "#DC2626",
      removeHoverBg: "rgba(220,38,38,0.08)",
      addCompareText: "#374151",
      addCompareDisabledText: "#9CA3AF",
      addCompareBorder: "#E5E7EB",
      toggleText: "#6B7280",
      toggleBorder: "#E5E7EB",
      toggleSelectedText: "#7C3AED",
      toggleSelectedBg: "rgba(124,58,237,0.12)",
      label: "#6B7280",
      inputText: "#111827",
      inputBg: "#FFFFFF",
      inputBorder: "#D1D5DB",
      inputBorderHover: "#7C3AED",
      menuBg: "#FFFFFF",
      menuBorder: "#E5E7EB",
      menuShadow: "0 12px 32px rgba(15,23,42,0.12)",
      menuPrimaryText: "#111827",
      menuSecondaryText: "#6B7280",
      menuSubheaderBg: "#F9FAFB",
      menuSubheaderText: "#9CA3AF",
      sectionCardBg: "#F9FAFB",
      sectionCardBorder: "#E5E7EB",
      tableBg: "#FFFFFF",
      tableBorder: "#E5E7EB",
      tableHeadBg: "#F9FAFB",
      tableHeadText: "#6B7280",
      tableHeadBorder: "#E5E7EB",
      tableRowBorder: "#F3F4F6",
      lapTimeText: "#374151",
      lapGapText: "#6B7280",
      lapOut: "#D97706",
      lapOutSelected: "#B45309",
      lapOutSelectedBg: "rgba(245,158,11,0.12)",
      lapIn: "#6B7280",
      lapInSelected: "#374151",
      lapInSelectedBg: "rgba(148,163,184,0.12)",
      lapCrash: "#DC2626",
      lapCrashSelected: "#B91C1C",
      lapCrashSelectedBg: "rgba(239,68,68,0.12)",
      lapFlying: "#374151",
      lapFlyingSelected: "#7C3AED",
      lapFlyingSelectedBg: "rgba(124,58,237,0.12)",
      lapCompareSelectedBg: "rgba(245,158,11,0.1)",
      lapCompareHoverBg: "rgba(245,158,11,0.06)",
      lapPrimaryHoverBg: "rgba(124,58,237,0.08)",
      footageChipText: "#047857",
      footageChipBg: "rgba(16,185,129,0.12)",
      footageChipBorder: "rgba(16,185,129,0.25)",
      footageChipIcon: "#059669"
    }
  },
  // ── Footage upload drawer (right panel on Sessions map) ────────────────────
  footageChrome: {
    dark: {
      paperBg: "#010D18",
      border: "rgba(36,62,82,0.72)",
      headerBorder: "rgba(36,62,82,0.72)",
      title: "#FFFFFF",
      subtitle: "#94A3B8",
      body: "#94A3B8",
      sectionTitle: "#E2E8F0",
      cardBg: "rgba(3,18,32,0.78)",
      cardBgMuted: "rgba(3,18,32,0.58)",
      cardBorder: "rgba(36,62,82,0.72)",
      label: "#94A3B8",
      value: "#E5F0FF",
      monoValue: "#CBD5E1",
      fileName: "#F8FAFC",
      expandIcon: "#DCEBFF",
      expandBorder: "rgba(148,163,184,0.28)",
      deleteIcon: "#FCA5A5",
      deleteBorder: "rgba(248,113,113,0.35)",
      sourceChipText: "#DCEBFF",
      sourceChipBg: "rgba(52,211,153,0.14)",
      matchChipText: "#D1FAE5",
      matchChipBg: "rgba(16,185,129,0.16)",
      neutralChipText: "#E2E8F0",
      neutralChipBg: "rgba(148,163,184,0.12)",
      inputLabel: "#94A3B8",
      inputLabelFocus: "#A78BFA",
      inputText: "#E5F0FF",
      inputBg: "rgba(15,23,42,0.62)",
      inputBorder: "rgba(71,85,105,0.72)",
      inputBorderHover: "rgba(167,139,250,0.78)",
      inputBorderFocus: "#8B5CF6",
      helperText: "#94A3B8"
    },
    light: {
      paperBg: "#FFFFFF",
      border: "#E5E7EB",
      headerBorder: "#E5E7EB",
      title: "#111827",
      subtitle: "#6B7280",
      body: "#6B7280",
      sectionTitle: "#111827",
      cardBg: "#F9FAFB",
      cardBgMuted: "#FFFFFF",
      cardBorder: "#E5E7EB",
      label: "#6B7280",
      value: "#374151",
      monoValue: "#4B5563",
      fileName: "#111827",
      expandIcon: "#374151",
      expandBorder: "#E5E7EB",
      deleteIcon: "#DC2626",
      deleteBorder: "rgba(220,38,38,0.25)",
      sourceChipText: "#047857",
      sourceChipBg: "rgba(16,185,129,0.12)",
      matchChipText: "#047857",
      matchChipBg: "rgba(16,185,129,0.12)",
      neutralChipText: "#4B5563",
      neutralChipBg: "#F3F4F6",
      inputLabel: "#6B7280",
      inputLabelFocus: "#7C3AED",
      inputText: "#111827",
      inputBg: "#FFFFFF",
      inputBorder: "#D1D5DB",
      inputBorderHover: "#7C3AED",
      inputBorderFocus: "#7C3AED",
      helperText: "#6B7280"
    }
  },
  // ── Lap traces panel (bottom sheet on Sessions map) ────────────────────────
  traceChrome: {
    dark: {
      panelBg: "rgba(1,13,24,0.96)",
      shellBg: "#010D18",
      shellHeaderBg: "rgba(1,13,24,0.98)",
      border: "rgba(36,62,82,0.82)",
      headerBorder: "rgba(36,62,82,0.54)",
      title: "#FFFFFF",
      subtitle: "#64748B",
      sectionLabel: "#A6B0BE",
      meta: "#64748B",
      chartSubtitle: "#A6B0BE",
      liveValue: "#FFFFFF",
      chartRowBorder: "rgba(36,62,82,0.42)",
      gridStroke: "rgba(255,255,255,0.04)",
      tooltipBg: "rgba(1,13,24,0.96)",
      tooltipBorder: "rgba(36,62,82,0.78)",
      cursorLine: "rgba(255,255,255,0.88)",
      cursorHoverLine: "rgba(148,163,184,0.55)",
      cursorLabelBg: "rgba(1,13,24,0.94)",
      cursorLabelBorder: "rgba(36,62,82,0.78)",
      cursorLabelText: "#FFFFFF",
      pickerBg: "#010D18",
      pickerBorder: "rgba(36,62,82,0.82)",
      pickerItemText: "#E2E8F0",
      pickerItemSubtext: "#64748B",
      pickerItemBorder: "rgba(36,62,82,0.22)",
      pickerItemHover: "rgba(255,255,255,0.04)",
      pickerItemSelectedBg: "rgba(183,140,255,0.12)",
      pickerItemSelectedBorder: "rgba(183,140,255,0.28)",
      pickerCheckbox: "#64748B",
      tracesButtonBorder: "rgba(36,62,82,0.72)",
      tracesButtonBg: "rgba(1,13,24,0.5)",
      tracesButtonActiveBg: "rgba(124,58,237,0.9)",
      tracesButtonHoverBorder: "rgba(183,140,255,0.68)",
      tracesButtonHoverBg: "rgba(255,255,255,0.05)",
      iconAccent: "#A855F7",
      closeIcon: "#A6B0BE",
      closeBorder: "rgba(36,62,82,0.72)",
      emptyText: "#64748B"
    },
    light: {
      panelBg: "#FFFFFF",
      shellBg: "#FFFFFF",
      shellHeaderBg: "#FFFFFF",
      border: "#E5E7EB",
      headerBorder: "#E5E7EB",
      title: "#111827",
      subtitle: "#6B7280",
      sectionLabel: "#6B7280",
      meta: "#6B7280",
      chartSubtitle: "#6B7280",
      liveValue: "#111827",
      chartRowBorder: "#F3F4F6",
      gridStroke: "rgba(15,23,42,0.08)",
      tooltipBg: "#FFFFFF",
      tooltipBorder: "#E5E7EB",
      cursorLine: "#7C3AED",
      cursorHoverLine: "rgba(124,58,237,0.45)",
      cursorLabelBg: "#FFFFFF",
      cursorLabelBorder: "#E5E7EB",
      cursorLabelText: "#111827",
      pickerBg: "#FFFFFF",
      pickerBorder: "#E5E7EB",
      pickerItemText: "#111827",
      pickerItemSubtext: "#6B7280",
      pickerItemBorder: "#E5E7EB",
      pickerItemHover: "rgba(0,0,0,0.03)",
      pickerItemSelectedBg: "#F8FAFC",
      pickerItemSelectedBorder: "#CBD5E1",
      pickerCheckbox: "#9CA3AF",
      tracesButtonBorder: "#E5E7EB",
      tracesButtonBg: "#FFFFFF",
      tracesButtonActiveBg: "#F3F4F6",
      tracesButtonHoverBorder: "#D1D5DB",
      tracesButtonHoverBg: "#F9FAFB",
      iconAccent: "#7C3AED",
      closeIcon: "#6B7280",
      closeBorder: "#E5E7EB",
      emptyText: "#6B7280"
    }
  },
  // ── Racing status chips ───────────────────────────────────────────────────
  status: {
    active: { text: "#22c55e", bg: "#22c55e1a", border: "#22c55e33" },
    injured: { text: "#ef4444", bg: "#ef44441a", border: "#ef444433" },
    inactive: { text: "#9ca3af", bg: "#9ca3af1a", border: "#9ca3af33" },
    inUse: { text: "#22c55e", bg: "#22c55e1a", border: "#22c55e33" },
    available: { text: "#9ca3af", bg: "#9ca3af1a", border: "#9ca3af33" },
    maintenance: { text: "#f97316", bg: "#f973161a", border: "#f9731633" },
    inShop: { text: "#f97316", bg: "#f973161a", border: "#f9731633" },
    overdue: { text: "#ef4444", bg: "#ef44441a", border: "#ef444433" },
    due5days: { text: "#f97316", bg: "#f973161a", border: "#f9731633" },
    due7days: { text: "#eab308", bg: "#eab3081a", border: "#eab30833" },
    race: { text: "#7C3AED", bg: "#7C3AED1a", border: "#7C3AED33" },
    practice: { text: "#0ea5e9", bg: "#0ea5e91a", border: "#0ea5e933" },
    test: { text: "#6b7280", bg: "#6b72801a", border: "#6b728033" },
    qualifying: { text: "#a78bfa", bg: "#a78bfa1a", border: "#a78bfa33" },
    positive: { text: "#22c55e", bg: "#22c55e1a", border: "#22c55e33" },
    neutral: { text: "#9ca3af", bg: "#9ca3af1a", border: "#9ca3af33" },
    needsWork: { text: "#f97316", bg: "#f973161a", border: "#f9731633" }
  }
};
function getSidebarChrome(isDark) {
  return isDark ? colors.sidebarChrome.dark : colors.sidebarChrome.light;
}
var typography = {
  fontFamilyBase: '"Inter", "Roboto", "Helvetica Neue", Arial, sans-serif',
  fontFamilyMono: '"JetBrains Mono", "Fira Code", "Courier New", monospace',
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightSemiBold: 600,
  fontWeightBold: 700,
  fontSizeBase: 14,
  scale: {
    xs: "0.75rem",
    // 12px
    sm: "0.875rem",
    // 14px
    md: "1rem",
    // 16px
    lg: "1.125rem",
    // 18px
    xl: "1.25rem",
    // 20px
    "2xl": "1.5rem",
    // 24px
    "3xl": "1.875rem",
    // 30px
    "4xl": "2.25rem",
    // 36px
    "5xl": "3rem"
    // 48px
  }
};
var spacingUnit = 8;
var shape = {
  borderRadius: 12,
  borderRadiusSm: 6,
  borderRadiusMd: 12,
  borderRadiusLg: 20,
  borderRadiusFull: 9999
};
var shadows = {
  card: "0 2px 16px rgba(0,0,0,0.4)",
  elevated: "0 8px 32px rgba(0,0,0,0.6)",
  glow: `0 0 20px rgba(76,175,80,0.3)`
};
var zIndex = {
  sidebar: 1200,
  header: 1100,
  modal: 1300,
  tooltip: 1500,
  snackbar: 1400
};
var breakpoints = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536
};

// src/theme/mui-theme.ts
function buildThemeOptions(mode) {
  const isDark = mode === "dark";
  const surface = isDark ? colors.dark : colors.light;
  return {
    palette: {
      mode,
      primary: {
        light: colors.primary[400],
        main: colors.primary[500],
        dark: colors.primary[700],
        contrastText: "#ffffff"
      },
      secondary: {
        light: colors.secondary[400],
        main: colors.secondary[500],
        dark: colors.secondary[700],
        contrastText: "#000000"
      },
      error: colors.error,
      warning: colors.warning,
      info: colors.info,
      success: colors.success,
      background: {
        default: surface.bg,
        paper: surface.surface
      },
      text: isDark ? { primary: "#FFFFFF", secondary: "rgba(255,255,255,0.60)", disabled: "rgba(255,255,255,0.35)" } : { primary: "#111827", secondary: "#6b7280", disabled: "#9ca3af" },
      grey: colors.grey,
      divider: surface.border,
      subtleFill: {
        main: isDark ? "rgba(255,255,255,0.04)" : colors.grey[50],
        light: isDark ? "rgba(255,255,255,0.025)" : "#F9FAFB",
        dark: isDark ? "rgba(255,255,255,0.06)" : colors.grey[100]
      }
    },
    typography: {
      fontFamily: typography.fontFamilyBase,
      fontWeightLight: typography.fontWeightLight,
      fontWeightRegular: typography.fontWeightRegular,
      fontWeightMedium: typography.fontWeightMedium,
      fontWeightBold: typography.fontWeightBold,
      h1: { fontSize: typography.scale["5xl"], fontWeight: typography.fontWeightBold, lineHeight: 1.2 },
      h2: { fontSize: typography.scale["4xl"], fontWeight: typography.fontWeightBold, lineHeight: 1.25 },
      h3: { fontSize: typography.scale["3xl"], fontWeight: typography.fontWeightSemiBold, lineHeight: 1.3 },
      h4: { fontSize: typography.scale["2xl"], fontWeight: typography.fontWeightSemiBold, lineHeight: 1.35 },
      h5: { fontSize: typography.scale.xl, fontWeight: typography.fontWeightMedium, lineHeight: 1.4 },
      h6: { fontSize: typography.scale.lg, fontWeight: typography.fontWeightSemiBold, lineHeight: 1.4 },
      subtitle1: { fontSize: typography.scale.md, fontWeight: typography.fontWeightMedium },
      subtitle2: { fontSize: typography.scale.sm, fontWeight: typography.fontWeightMedium },
      body1: { fontSize: typography.scale.md, lineHeight: 1.6 },
      body2: { fontSize: typography.scale.sm, lineHeight: 1.6 },
      caption: { fontSize: typography.scale.xs },
      overline: { fontSize: "0.6875rem", fontWeight: typography.fontWeightSemiBold, letterSpacing: "0.08em", textTransform: "uppercase" },
      button: { fontSize: typography.scale.sm, fontWeight: typography.fontWeightSemiBold, textTransform: "none" }
    },
    spacing: spacingUnit,
    shape: { borderRadius: shape.borderRadius },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          "*": { boxSizing: "border-box" },
          html: {
            fontSize: "90%"
          },
          body: {
            scrollbarWidth: "thin",
            scrollbarColor: `${isDark ? colors.dark.elevated : colors.grey[300]} transparent`,
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": { borderRadius: 3, backgroundColor: isDark ? colors.dark.elevated : colors.grey[300] }
          }
        }
      },
      // ── Button ──────────────────────────────────────────────────────────────
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: shape.borderRadiusSm,
            fontWeight: typography.fontWeightSemiBold,
            paddingTop: 7,
            paddingBottom: 7
          },
          containedPrimary: {
            background: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[600]} 100%)`,
            color: "#ffffff",
            "& .MuiButton-startIcon": { color: "#ffffff" },
            "&:hover": {
              background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
              color: "#ffffff"
            },
            "&.Mui-disabled": {
              color: "rgba(255,255,255,0.55)",
              "& .MuiButton-startIcon": { color: "rgba(255,255,255,0.55)" }
            }
          },
          outlinedPrimary: {
            borderColor: `${colors.primary[500]}66`,
            "&:hover": { borderColor: colors.primary[500], backgroundColor: `${colors.primary[500]}11` }
          }
        }
      },
      // ── Card ────────────────────────────────────────────────────────────────
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: surface.surface,
            border: `1px solid ${surface.border}`,
            borderRadius: shape.borderRadiusMd
          }
        }
      },
      // ── Paper ───────────────────────────────────────────────────────────────
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: surface.surface,
            border: `1px solid ${surface.border}`
          }
        }
      },
      // ── AppBar ──────────────────────────────────────────────────────────────
      MuiAppBar: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: surface.sidebar,
            borderBottom: `1px solid ${surface.border}`,
            boxShadow: "none"
          }
        }
      },
      // ── Drawer ──────────────────────────────────────────────────────────────
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundImage: "none",
            backgroundColor: surface.sidebar,
            borderRight: `1px solid ${surface.border}`,
            boxShadow: "none"
          }
        }
      },
      // ── ListItemButton (nav items) ──────────────────────────────────────────
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: shape.borderRadiusSm,
            marginBottom: 2,
            paddingTop: 7,
            paddingBottom: 7,
            "&.Mui-selected": {
              backgroundColor: `${colors.primary[500]}1a`,
              color: colors.primary[isDark ? 400 : 600],
              "& .MuiListItemIcon-root": { color: colors.primary[isDark ? 400 : 600] },
              "&:hover": { backgroundColor: `${colors.primary[500]}26` }
            },
            "&:hover": { backgroundColor: surface.hover }
          }
        }
      },
      MuiListItemIcon: {
        styleOverrides: { root: { minWidth: 34, color: isDark ? "rgba(255,255,255,0.45)" : colors.grey[500] } }
      },
      // ── Chip ────────────────────────────────────────────────────────────────
      MuiChip: {
        styleOverrides: {
          root: ({ ownerState }) => ({
            borderRadius: shape.borderRadiusSm,
            ...ownerState.variant === "outlined" && {
              borderColor: isDark ? "rgba(255,255,255,0.12)" : colors.grey[200],
              ...ownerState.color === "success" && {
                color: isDark ? colors.success.light : colors.success.dark,
                borderColor: isDark ? "rgba(34,197,94,0.28)" : "rgba(34,197,94,0.24)",
                bgcolor: isDark ? "rgba(34,197,94,0.08)" : "rgba(34,197,94,0.08)",
                "& .MuiChip-icon": { color: isDark ? colors.success.light : colors.success.dark }
              },
              ...(ownerState.color === "default" || ownerState.color === void 0) && {
                color: isDark ? "rgba(255,255,255,0.72)" : colors.grey[700],
                bgcolor: isDark ? "rgba(255,255,255,0.03)" : colors.grey[50],
                "& .MuiChip-icon": { color: isDark ? "rgba(255,255,255,0.55)" : colors.grey[500] }
              }
            }
          })
        }
      },
      // ── TextField ───────────────────────────────────────────────────────────
      MuiTextField: {
        defaultProps: { variant: "outlined", size: "small" },
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: shape.borderRadiusSm,
              backgroundColor: isDark ? "rgba(255,255,255,0.03)" : colors.grey[50],
              "& fieldset": { borderColor: surface.border },
              "&:hover fieldset": { borderColor: isDark ? "rgba(255,255,255,0.2)" : colors.grey[400] }
            }
          }
        }
      },
      // ── Select ──────────────────────────────────────────────────────────────
      MuiSelect: {
        defaultProps: { size: "small" },
        styleOverrides: {
          outlined: {
            borderRadius: shape.borderRadiusSm
          }
        }
      },
      // ── Table ───────────────────────────────────────────────────────────────
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottomColor: surface.border, padding: "10px 16px" },
          head: {
            fontWeight: typography.fontWeightSemiBold,
            color: isDark ? "rgba(255,255,255,0.45)" : colors.grey[500],
            fontSize: "0.7rem",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            backgroundColor: isDark ? colors.dark.elevated : colors.grey[50]
          }
        }
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&:hover": { backgroundColor: surface.hover },
            "&:last-child td": { borderBottom: "none" }
          }
        }
      },
      // ── Tooltip ─────────────────────────────────────────────────────────────
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? colors.dark.elevated : colors.grey[800],
            border: `1px solid ${surface.border}`,
            fontSize: typography.scale.xs,
            borderRadius: shape.borderRadiusSm
          }
        }
      },
      // ── Divider ─────────────────────────────────────────────────────────────
      MuiDivider: {
        styleOverrides: { root: { borderColor: surface.border } }
      },
      // ── Avatar ──────────────────────────────────────────────────────────────
      MuiAvatar: {
        styleOverrides: {
          root: {
            backgroundColor: `${colors.primary[500]}33`,
            color: colors.primary[400],
            fontWeight: typography.fontWeightSemiBold
          }
        }
      },
      // ── LinearProgress ──────────────────────────────────────────────────────
      MuiLinearProgress: {
        styleOverrides: {
          root: { borderRadius: shape.borderRadiusFull, height: 6 }
        }
      },
      // ── Menu ────────────────────────────────────────────────────────────────
      MuiMenu: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          paper: {
            border: `1px solid ${surface.border}`,
            borderRadius: shape.borderRadiusMd,
            backgroundColor: isDark ? colors.dark.elevated : "#ffffff"
          }
        }
      },
      // ── Dialog ──────────────────────────────────────────────────────────────
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundImage: "none",
            backgroundColor: isDark ? colors.dark.elevated : "#ffffff",
            border: `1px solid ${surface.border}`,
            borderRadius: shape.borderRadiusMd
          }
        }
      },
      // ── Tabs ────────────────────────────────────────────────────────────────
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: typography.fontWeightMedium,
            fontSize: typography.scale.sm,
            minHeight: 40,
            "&.Mui-selected": { fontWeight: typography.fontWeightSemiBold }
          }
        }
      },
      MuiTabs: {
        styleOverrides: {
          indicator: { height: 2, borderRadius: 2 },
          root: { minHeight: 40 }
        }
      }
    }
  };
}
var darkTheme = createTheme(buildThemeOptions("dark"));
var lightTheme = createTheme(buildThemeOptions("light"));
var theme = darkTheme;

// src/theme/headerStyles.ts
function getHeaderChrome(isDark) {
  return isDark ? colors.headerChrome.dark : colors.headerChrome.light;
}
function getLapMetaChrome(isDark) {
  return isDark ? colors.lapMetaChrome.dark : colors.lapMetaChrome.light;
}
function getFootageChrome(isDark) {
  return isDark ? colors.footageChrome.dark : colors.footageChrome.light;
}
function getTraceChrome(isDark) {
  return isDark ? colors.traceChrome.dark : colors.traceChrome.light;
}
function getSessionDrawerChrome(isDark) {
  return isDark ? colors.sessionDrawerChrome.dark : colors.sessionDrawerChrome.light;
}
function headerOutlinedButtonSx(isDark) {
  const chrome = getHeaderChrome(isDark);
  return {
    borderColor: chrome.buttonBorder,
    color: chrome.buttonText,
    bgcolor: chrome.buttonBg,
    "&:hover": {
      borderColor: chrome.buttonBorderHover,
      bgcolor: chrome.buttonBgHover
    }
  };
}
function headerSectionLabelSx(isDark) {
  const chrome = getHeaderChrome(isDark);
  return {
    fontSize: "0.72rem",
    fontWeight: 750,
    color: chrome.sectionLabel,
    letterSpacing: "0.04em",
    lineHeight: 1.25,
    textTransform: "uppercase",
    whiteSpace: "nowrap"
  };
}
function headerReplayBarSimpleSx(isDark) {
  const chrome = getHeaderChrome(isDark);
  return {
    display: "flex",
    alignItems: "center",
    gap: 0.8,
    width: "100%",
    minWidth: 0,
    height: 38,
    px: 0.65,
    py: 0.45,
    borderRadius: "8px",
    border: `1px solid ${chrome.replayBarBorder}`,
    bgcolor: chrome.replayBarBg,
    boxShadow: chrome.replayBarShadow
  };
}
function headerReplayPlayButtonSx(isDark, isPlaying) {
  const chrome = getHeaderChrome(isDark);
  return {
    width: 28,
    height: 28,
    flexShrink: 0,
    border: `1px solid ${chrome.replayPlayBorder}`,
    color: isDark ? "#FFFFFF" : colors.primary[600],
    bgcolor: isPlaying ? chrome.replayPlayBgActive : chrome.replayPlayBg,
    "&:hover": { bgcolor: chrome.replayPlayBgHover }
  };
}
function headerWeatherChipSx(isDark, variant = "inline") {
  const chrome = getHeaderChrome(isDark);
  if (variant === "header") {
    return {
      display: "inline-flex",
      alignItems: "center",
      gap: 1,
      minHeight: 40,
      px: 0.25,
      py: 0,
      cursor: "pointer",
      minWidth: 0,
      maxWidth: 260,
      flexShrink: 0,
      border: "none",
      bgcolor: "transparent",
      borderRadius: "8px",
      "&:hover": {
        bgcolor: chrome.weatherHoverBg
      }
    };
  }
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.75,
    px: 1,
    py: 0.45,
    borderRadius: "8px",
    border: "none",
    bgcolor: "transparent",
    cursor: "pointer",
    minWidth: 128,
    maxWidth: 200,
    "&:hover": {
      bgcolor: chrome.weatherHoverBg
    }
  };
}
function headerWeatherPanelSx(isDark) {
  const chrome = getHeaderChrome(isDark);
  return {
    display: { xs: "none", lg: "flex" },
    alignItems: "center",
    flexShrink: 0,
    ml: "auto",
    pl: 1.5,
    borderLeft: `1px solid ${chrome.weatherDivider}`
  };
}
function mapPlaybackDockOuterSx(isDark) {
  return {
    position: "absolute",
    left: { xs: 8, md: 12 },
    right: { xs: 8, md: 12 },
    bottom: { xs: 8, md: 12 },
    zIndex: 8,
    pointerEvents: "none"
  };
}
function mapPlaybackDockShellSx(isDark) {
  const chrome = getHeaderChrome(isDark);
  return {
    pointerEvents: "auto",
    borderRadius: "10px",
    border: `1px solid ${chrome.mapDockBorder}`,
    bgcolor: chrome.mapDockBg,
    boxShadow: chrome.mapDockShadow,
    backdropFilter: "blur(10px)",
    overflow: "hidden"
  };
}
function mapPlaybackDockLegendSx(isDark) {
  const chrome = getHeaderChrome(isDark);
  return {
    px: 1.1,
    py: 0.6,
    borderBottom: `1px solid ${chrome.mapDockBorder}`,
    bgcolor: chrome.mapDockLegendBg
  };
}
function mapPlaybackDockReplaySx() {
  return {
    px: 0.75,
    py: 0.75
  };
}
function lapMetaDrawerPaperSx(isDark) {
  const chrome = getLapMetaChrome(isDark);
  return {
    maxHeight: { xs: "72vh", md: "52vh" },
    bgcolor: chrome.paperBg,
    borderTop: `1px solid ${chrome.border}`,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    overflow: "hidden"
  };
}
function lapMetaDrawerHeaderSx(isDark) {
  const chrome = getLapMetaChrome(isDark);
  return {
    px: { xs: 1.5, md: 2 },
    py: 1.25,
    borderBottom: `1px solid ${chrome.headerBorder}`
  };
}
function lapMetaToggleGroupSx(isDark) {
  const chrome = getLapMetaChrome(isDark);
  return {
    flexShrink: 0,
    "& .MuiToggleButton-root": {
      width: 76,
      px: 1,
      py: 0.35,
      fontSize: "0.68rem",
      fontWeight: 800,
      textTransform: "none",
      color: chrome.toggleText,
      borderColor: chrome.toggleBorder,
      "&.Mui-selected": {
        color: chrome.toggleSelectedText,
        bgcolor: chrome.toggleSelectedBg
      },
      "&.Mui-selected:hover": {
        bgcolor: chrome.toggleSelectedBg
      }
    }
  };
}
function lapMetaTableContainerSx(isDark) {
  const chrome = getLapMetaChrome(isDark);
  return {
    maxHeight: { xs: "54vh", md: "38vh" },
    border: `1px solid ${chrome.tableBorder}`,
    borderRadius: 1,
    bgcolor: chrome.tableBg
  };
}
function footageDrawerPaperSx(isDark) {
  const chrome = getFootageChrome(isDark);
  return {
    width: { xs: "100%", sm: 420 },
    maxWidth: "100vw",
    bgcolor: chrome.paperBg,
    borderLeft: `1px solid ${chrome.border}`,
    color: chrome.title
  };
}
function footageDrawerHeaderSx(isDark) {
  const chrome = getFootageChrome(isDark);
  return {
    flexShrink: 0,
    px: 1.5,
    py: 1.25,
    borderBottom: `1px solid ${chrome.headerBorder}`
  };
}
function footageCardSx(isDark, muted = false) {
  const chrome = getFootageChrome(isDark);
  return {
    border: `1px solid ${chrome.cardBorder}`,
    borderRadius: 1,
    bgcolor: muted ? chrome.cardBgMuted : chrome.cardBg,
    p: muted ? 1 : 1.25
  };
}
function footageTextFieldSx(isDark) {
  const chrome = getFootageChrome(isDark);
  return {
    "& .MuiInputLabel-root": { color: chrome.inputLabel, fontSize: "0.72rem" },
    "& .MuiInputLabel-root.Mui-focused": { color: chrome.inputLabelFocus },
    "& .MuiInputBase-root": {
      color: chrome.inputText,
      fontFamily: "monospace",
      fontSize: "0.72rem",
      bgcolor: chrome.inputBg
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: chrome.inputBorder },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: chrome.inputBorderHover },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: chrome.inputBorderFocus },
    "& .MuiFormHelperText-root": { color: chrome.helperText, mx: 0, fontSize: "0.62rem" }
  };
}
function uploadDialogPaperSx(isDark) {
  const chrome = getFootageChrome(isDark);
  return {
    bgcolor: chrome.paperBg,
    color: chrome.title,
    border: `1px solid ${chrome.border}`,
    borderRadius: 2,
    backgroundImage: "none"
  };
}
function uploadDialogSelectSx(isDark) {
  const chrome = getFootageChrome(isDark);
  return {
    color: chrome.inputText,
    fontSize: "0.78rem",
    bgcolor: chrome.inputBg,
    ".MuiOutlinedInput-notchedOutline": { borderColor: chrome.inputBorder },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: chrome.inputBorderHover },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: chrome.inputBorderFocus }
  };
}
function uploadChoiceButtonSx(isDark) {
  const chrome = getFootageChrome(isDark);
  const trace = getTraceChrome(isDark);
  return {
    borderColor: chrome.cardBorder,
    bgcolor: isDark ? "rgba(255,255,255,0.025)" : chrome.cardBg,
    color: chrome.title,
    "& .MuiButton-startIcon": {
      mt: 0.2,
      color: trace.iconAccent
    },
    "&:hover": {
      borderColor: isDark ? "rgba(124,58,237,0.72)" : trace.iconAccent,
      bgcolor: isDark ? "rgba(124,58,237,0.08)" : "rgba(124,58,237,0.08)"
    }
  };
}
function sessionDrawerPaperSx(isDark) {
  const chrome = getSessionDrawerChrome(isDark);
  return {
    width: { xs: "100%", sm: 400 },
    maxWidth: "100vw",
    bgcolor: chrome.paperBg,
    backgroundColor: chrome.paperBg,
    backgroundImage: "none",
    borderLeft: `1px solid ${chrome.border}`,
    borderRight: "none",
    color: chrome.title
  };
}
function sessionDrawerHeaderSx(isDark) {
  const chrome = getSessionDrawerChrome(isDark);
  return {
    flexShrink: 0,
    px: 1.5,
    py: 1.25,
    borderBottom: `1px solid ${chrome.headerBorder}`
  };
}
function sessionDrawerSectionLabelSx(isDark) {
  const chrome = getSessionDrawerChrome(isDark);
  return {
    fontSize: "0.72rem",
    fontWeight: 800,
    color: chrome.sectionLabel,
    letterSpacing: "0.04em"
  };
}
function sessionDrawerResetButtonSx(isDark) {
  const chrome = getSessionDrawerChrome(isDark);
  return {
    minWidth: 0,
    px: 1,
    py: 0.35,
    textTransform: "none",
    fontSize: "0.68rem",
    fontWeight: 700,
    color: chrome.resetText,
    border: `1px solid ${chrome.resetBorder}`,
    borderRadius: "6px",
    "&:hover": {
      bgcolor: chrome.resetHoverBg,
      borderColor: chrome.inputBorderHover
    }
  };
}
function sessionDrawerIconButtonSx(isDark) {
  const chrome = getSessionDrawerChrome(isDark);
  return {
    color: chrome.closeText,
    border: `1px solid ${chrome.closeBorder}`,
    borderRadius: "6px"
  };
}
function sessionDrawerToggleGroupSx(isDark) {
  const chrome = getSessionDrawerChrome(isDark);
  return {
    "& .MuiToggleButton-root": {
      px: 1.25,
      py: 0.35,
      fontSize: "0.68rem",
      fontWeight: 750,
      textTransform: "none",
      color: chrome.toggleText,
      borderColor: chrome.toggleBorder,
      "&.Mui-selected": {
        color: chrome.toggleSelectedText,
        bgcolor: chrome.toggleSelectedBg
      }
    }
  };
}
function sessionDrawerSelectSx(isDark) {
  const chrome = getSessionDrawerChrome(isDark);
  return {
    color: chrome.inputText,
    bgcolor: chrome.inputBg,
    fontSize: "0.78rem",
    ".MuiOutlinedInput-notchedOutline": { borderColor: chrome.inputBorder },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: chrome.inputBorderHover },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: chrome.inputBorderHover }
  };
}
function sessionDrawerCardSx(isDark) {
  const chrome = getSessionDrawerChrome(isDark);
  return {
    border: `1px solid ${chrome.cardBorder}`,
    borderRadius: 1.25,
    bgcolor: chrome.cardBg
  };
}
function sessionDrawerCollapsibleSectionSx(isDark) {
  const chrome = getSessionDrawerChrome(isDark);
  return {
    border: `1px solid ${chrome.sectionCardBorder}`,
    borderRadius: "8px",
    p: 1.25,
    bgcolor: chrome.sectionCardBg
  };
}
function sessionDrawerSelectMenuButtonSx(isDark, open) {
  const chrome = getSessionDrawerChrome(isDark);
  return {
    justifyContent: "space-between",
    px: 1.25,
    py: 0.85,
    textTransform: "none",
    borderColor: open ? colors.primary[500] : chrome.inputBorder,
    color: chrome.menuPrimaryText,
    bgcolor: chrome.inputBg,
    "&:hover": {
      borderColor: chrome.inputBorderHover,
      bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#F9FAFB"
    }
  };
}
function sessionDrawerSelectMenuPaperSx(isDark) {
  const chrome = getSessionDrawerChrome(isDark);
  return {
    bgcolor: chrome.menuBg,
    color: chrome.menuPrimaryText,
    border: `1px solid ${chrome.menuBorder}`,
    borderRadius: 1,
    boxShadow: chrome.menuShadow
  };
}
function sessionDrawerLapTableShellSx(isDark) {
  const chrome = getSessionDrawerChrome(isDark);
  return {
    borderRadius: 1.25,
    border: `1px solid ${chrome.tableBorder}`,
    overflow: "hidden",
    bgcolor: chrome.tableBg
  };
}
function sessionDrawerCompareAddButtonSx(isDark, enabled) {
  const chrome = getSessionDrawerChrome(isDark);
  return {
    justifyContent: "flex-start",
    py: 1.1,
    px: 1.25,
    textTransform: "none",
    fontSize: "0.78rem",
    fontWeight: 700,
    color: enabled ? chrome.addCompareText : chrome.addCompareDisabledText,
    borderColor: chrome.addCompareBorder,
    borderStyle: "dashed",
    borderRadius: 1.25,
    "&:hover": {
      borderColor: colors.primary[500],
      bgcolor: `${colors.primary[500]}12`,
      borderStyle: "dashed"
    }
  };
}
function sessionDrawerLapToneStyles(isDark, kind, selected) {
  const chrome = getSessionDrawerChrome(isDark);
  switch (kind) {
    case "out":
      return {
        color: selected ? chrome.lapOutSelected : chrome.lapOut,
        bg: selected ? chrome.lapOutSelectedBg : "transparent"
      };
    case "in":
      return {
        color: selected ? chrome.lapInSelected : chrome.lapIn,
        bg: selected ? chrome.lapInSelectedBg : "transparent"
      };
    case "crash":
      return {
        color: selected ? chrome.lapCrashSelected : chrome.lapCrash,
        bg: selected ? chrome.lapCrashSelectedBg : "transparent"
      };
    default:
      return {
        color: selected ? chrome.lapFlyingSelected : chrome.lapFlying,
        bg: selected ? chrome.lapFlyingSelectedBg : "transparent"
      };
  }
}
function traceDrawerShellSx(isDark) {
  const chrome = getTraceChrome(isDark);
  return {
    bgcolor: chrome.shellBg,
    color: chrome.title,
    borderTop: `1px solid ${chrome.border}`,
    overflow: "hidden"
  };
}
function traceDrawerHeaderSx(isDark) {
  const chrome = getTraceChrome(isDark);
  return {
    alignItems: "center",
    justifyContent: "space-between",
    px: 1.4,
    py: 0.9,
    borderBottom: `1px solid ${chrome.headerBorder}`,
    bgcolor: chrome.shellHeaderBg
  };
}
function tracePanelSx(isDark, drawer = false) {
  const chrome = getTraceChrome(isDark);
  return {
    flexShrink: 0,
    borderTop: drawer ? 0 : `1px solid ${chrome.border}`,
    bgcolor: chrome.panelBg,
    px: drawer ? 1.4 : 1.2,
    py: drawer ? 1 : 0.75,
    maxHeight: drawer ? "min(48vh, 420px)" : 240,
    minHeight: 0,
    overflow: "hidden"
  };
}
function tracePickerDrawerPaperSx(isDark) {
  const chrome = getTraceChrome(isDark);
  return {
    width: { xs: "86vw", sm: 340 },
    maxWidth: "100vw",
    bgcolor: chrome.pickerBg,
    color: chrome.title,
    borderLeft: `1px solid ${chrome.pickerBorder}`,
    boxShadow: isDark ? "0 0 36px rgba(0,0,0,0.46)" : "0 8px 24px rgba(15,23,42,0.12)"
  };
}
function mapOverlayButtonSx(isDark, active = false) {
  const chrome = getHeaderChrome(isDark);
  const base = {
    height: 30,
    px: 1.1,
    borderRadius: active ? "8px" : "6px",
    textTransform: "none",
    fontSize: "0.74rem",
    fontWeight: 850,
    flexShrink: 0
  };
  if (active) {
    return {
      ...base,
      bgcolor: isDark ? "rgba(183,140,255,0.88)" : "rgba(124,58,237,0.14)",
      color: isDark ? "#FFFFFF" : colors.primary[700],
      border: isDark ? "1px solid rgba(183,140,255,0.45)" : `1px solid ${colors.primary[200]}`,
      boxShadow: isDark ? "0 8px 22px rgba(0,0,0,0.3)" : "none",
      "&:hover": {
        bgcolor: isDark ? "rgba(183,140,255,0.96)" : "rgba(124,58,237,0.2)"
      }
    };
  }
  return {
    ...base,
    bgcolor: isDark ? "rgba(1,13,24,0.82)" : chrome.buttonBg,
    color: isDark ? "#FFFFFF" : chrome.buttonText,
    border: `1px solid ${isDark ? "rgba(36,62,82,0.72)" : chrome.buttonBorder}`,
    boxShadow: isDark ? "0 8px 22px rgba(0,0,0,0.3)" : "none",
    "&:hover": {
      bgcolor: isDark ? "rgba(12,27,42,0.94)" : chrome.buttonBgHover,
      borderColor: isDark ? "rgba(36,62,82,0.72)" : chrome.buttonBorderHover
    },
    "&.Mui-disabled": {
      color: isDark ? "rgba(255,255,255,0.48)" : "#9CA3AF",
      bgcolor: isDark ? "rgba(1,13,24,0.62)" : "#F9FAFB"
    }
  };
}

// src/components/GridlineProvider.tsx
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { ThemeProvider, CssBaseline, GlobalStyles } from "@mui/material";
import { jsx, jsxs } from "react/jsx-runtime";
var COLOR_MODE_STORAGE_KEY = "paddiq:color-mode";
var INTERFACE_PREFERENCES_STORAGE_KEY = "paddiq:interface-preferences";
var DEFAULT_INTERFACE_PREFERENCES = {
  textSize: "default",
  density: "compact",
  sidebarDefault: "remember",
  reduceMotion: false,
  highContrast: false
};
var InterfacePreferencesContext = createContext({
  preferences: DEFAULT_INTERFACE_PREFERENCES,
  setPreference: () => void 0,
  resetPreferences: () => void 0
});
function useInterfacePreferences() {
  return useContext(InterfacePreferencesContext);
}
function loadInterfacePreferences() {
  if (typeof window === "undefined") {
    return DEFAULT_INTERFACE_PREFERENCES;
  }
  try {
    const stored = JSON.parse(window.localStorage.getItem(INTERFACE_PREFERENCES_STORAGE_KEY) ?? "{}");
    return { ...DEFAULT_INTERFACE_PREFERENCES, ...stored };
  } catch {
    return DEFAULT_INTERFACE_PREFERENCES;
  }
}
var ColorModeContext = createContext({
  mode: "system",
  resolvedMode: "dark",
  toggle: () => void 0,
  setMode: () => void 0
});
function useColorMode() {
  return useContext(ColorModeContext);
}
function isColorModePreference(value) {
  return value === "light" || value === "dark" || value === "system";
}
function loadStoredColorMode(defaultMode) {
  if (typeof window === "undefined") {
    return defaultMode;
  }
  const stored = window.localStorage.getItem(COLOR_MODE_STORAGE_KEY);
  return isColorModePreference(stored) ? stored : defaultMode;
}
function persistColorMode(mode) {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage.setItem(COLOR_MODE_STORAGE_KEY, mode);
}
function getSystemColorMode() {
  if (typeof window === "undefined") {
    return "dark";
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}
function PaddiqProvider({ children, defaultMode = "system" }) {
  const [mode, setModeState] = useState(() => loadStoredColorMode(defaultMode));
  const [systemMode, setSystemMode] = useState(() => getSystemColorMode());
  const [interfacePreferences, setInterfacePreferences] = useState(loadInterfacePreferences);
  useEffect(() => {
    if (typeof window === "undefined") {
      return void 0;
    }
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (event) => {
      setSystemMode(event.matches ? "dark" : "light");
    };
    setSystemMode(mediaQuery.matches ? "dark" : "light");
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);
  const resolvedMode = mode === "system" ? systemMode : mode;
  const theme2 = resolvedMode === "dark" ? darkTheme : lightTheme;
  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }
    document.documentElement.style.colorScheme = resolvedMode;
  }, [resolvedMode]);
  const setMode = useCallback((next) => {
    setModeState(next);
    persistColorMode(next);
  }, []);
  const ctx = useMemo(
    () => ({
      mode,
      resolvedMode,
      setMode,
      toggle: () => setMode(resolvedMode === "dark" ? "light" : "dark")
    }),
    [mode, resolvedMode, setMode]
  );
  const interfaceContext = useMemo(() => ({
    preferences: interfacePreferences,
    setPreference: (key, value) => {
      setInterfacePreferences((current) => {
        const next = { ...current, [key]: value };
        try {
          if (typeof window !== "undefined") {
            window.localStorage.setItem(INTERFACE_PREFERENCES_STORAGE_KEY, JSON.stringify(next));
          }
        } catch {
        }
        return next;
      });
    },
    resetPreferences: () => {
      setInterfacePreferences(DEFAULT_INTERFACE_PREFERENCES);
      try {
        if (typeof window !== "undefined") {
          window.localStorage.removeItem(INTERFACE_PREFERENCES_STORAGE_KEY);
        }
      } catch {
      }
    }
  }), [interfacePreferences]);
  useEffect(() => {
    const root = document.documentElement;
    root.style.fontSize = {
      default: "90%",
      large: "100%",
      "extra-large": "112.5%"
    }[interfacePreferences.textSize];
    root.dataset.paddiqDensity = interfacePreferences.density;
    root.dataset.paddiqContrast = String(interfacePreferences.highContrast);
  }, [interfacePreferences]);
  return /* @__PURE__ */ jsx(ColorModeContext.Provider, { value: ctx, children: /* @__PURE__ */ jsx(InterfacePreferencesContext.Provider, { value: interfaceContext, children: /* @__PURE__ */ jsxs(ThemeProvider, { theme: theme2, children: [
    /* @__PURE__ */ jsx(CssBaseline, {}),
    /* @__PURE__ */ jsx(GlobalStyles, { styles: {
      ...interfacePreferences.reduceMotion ? {
        "*, *::before, *::after": {
          animationDuration: "0.01ms !important",
          animationIterationCount: "1 !important",
          scrollBehavior: "auto !important",
          transitionDuration: "0.01ms !important"
        }
      } : {},
      ...interfacePreferences.density === "comfortable" ? {
        ".MuiButton-root, .MuiToggleButton-root": { minHeight: 40 },
        ".MuiInputBase-root": { minHeight: 42 },
        ".MuiMenuItem-root": { minHeight: 40 }
      } : {},
      ...interfacePreferences.highContrast ? {
        ".MuiPaper-root, .MuiCard-root, .MuiOutlinedInput-notchedOutline, .MuiDivider-root, .MuiToggleButton-root": {
          borderColor: `${resolvedMode === "dark" ? "rgba(255,255,255,0.34)" : "rgba(17,24,39,0.34)"} !important`
        },
        ".MuiTypography-colorTextSecondary": {
          color: `${resolvedMode === "dark" ? "rgba(255,255,255,0.82)" : "rgba(17,24,39,0.82)"} !important`
        }
      } : {}
    } }),
    children
  ] }) }) });
}
var GridlineProvider = PaddiqProvider;

// src/components/settings/ThemeModeSelect.tsx
import { IconButton, Stack } from "@mui/material";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import SettingsBrightnessOutlinedIcon from "@mui/icons-material/SettingsBrightnessOutlined";
import { jsx as jsx2 } from "react/jsx-runtime";
var MODE_OPTIONS = [
  { value: "light", label: "Light", icon: /* @__PURE__ */ jsx2(LightModeOutlinedIcon, { sx: { fontSize: 18 } }) },
  { value: "dark", label: "Dark", icon: /* @__PURE__ */ jsx2(DarkModeOutlinedIcon, { sx: { fontSize: 18 } }) },
  { value: "system", label: "System", icon: /* @__PURE__ */ jsx2(SettingsBrightnessOutlinedIcon, { sx: { fontSize: 18 } }) }
];
function ThemeModeSelect({ sx }) {
  const { mode, setMode } = useColorMode();
  return /* @__PURE__ */ jsx2(Stack, { direction: "row", spacing: 0.5, role: "group", "aria-label": "Appearance", sx, children: MODE_OPTIONS.map((option) => {
    const selected = mode === option.value;
    return /* @__PURE__ */ jsx2(
      IconButton,
      {
        size: "small",
        "aria-label": option.label,
        "aria-pressed": selected,
        onClick: () => setMode(option.value),
        sx: {
          borderRadius: 1,
          border: "1px solid",
          borderColor: selected ? "primary.main" : "divider",
          bgcolor: selected ? "primary.main" : "transparent",
          color: selected ? "primary.contrastText" : "text.secondary",
          "&:hover": {
            bgcolor: selected ? "primary.dark" : "action.hover"
          }
        },
        children: option.icon
      },
      option.value
    );
  }) });
}

// src/components/settings/InterfaceSettingsPanel.tsx
import {
  Box,
  Button,
  Divider,
  Stack as Stack2,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material";
import ContrastOutlinedIcon from "@mui/icons-material/ContrastOutlined";
import DensitySmallOutlinedIcon from "@mui/icons-material/DensitySmallOutlined";
import MotionPhotosOffOutlinedIcon from "@mui/icons-material/MotionPhotosOffOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import SettingsBrightnessOutlinedIcon2 from "@mui/icons-material/SettingsBrightnessOutlined";
import TextFieldsRoundedIcon from "@mui/icons-material/TextFieldsRounded";
import ViewSidebarOutlinedIcon from "@mui/icons-material/ViewSidebarOutlined";
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var rowSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "minmax(180px, 1fr) auto" },
  gap: { xs: 1, sm: 2 },
  alignItems: "center",
  py: 1.25
};
function SettingLabel({ icon, title, description }) {
  return /* @__PURE__ */ jsxs2(Stack2, { direction: "row", spacing: 1, sx: { alignItems: "flex-start", minWidth: 0 }, children: [
    /* @__PURE__ */ jsx3(Box, { sx: { display: "flex", color: "text.secondary", pt: 0.15, "& .MuiSvgIcon-root": { fontSize: 18 } }, children: icon }),
    /* @__PURE__ */ jsxs2(Box, { sx: { minWidth: 0 }, children: [
      /* @__PURE__ */ jsx3(Typography, { sx: { fontSize: "0.8rem", fontWeight: 800, lineHeight: 1.3 }, children: title }),
      /* @__PURE__ */ jsx3(Typography, { sx: { mt: 0.25, fontSize: "0.7rem", color: "text.secondary", lineHeight: 1.4 }, children: description })
    ] })
  ] });
}
function ChoiceControl({
  preferenceKey,
  options
}) {
  const { preferences, setPreference } = useInterfacePreferences();
  return /* @__PURE__ */ jsx3(
    ToggleButtonGroup,
    {
      exclusive: true,
      size: "small",
      value: preferences[preferenceKey],
      onChange: (_event, value) => {
        if (value !== null) setPreference(preferenceKey, value);
      },
      sx: {
        justifySelf: { sm: "end" },
        "& .MuiToggleButton-root": {
          minHeight: 30,
          px: 1,
          py: 0.45,
          borderColor: "divider",
          color: "text.secondary",
          fontSize: "0.68rem",
          fontWeight: 700,
          textTransform: "none",
          "&.Mui-selected": {
            bgcolor: "rgba(124,58,237,0.12)",
            color: colors.primary[200]
          }
        }
      },
      children: options.map((option) => /* @__PURE__ */ jsx3(ToggleButton, { value: option.value, children: option.label }, String(option.value)))
    }
  );
}
function InterfaceSettingsPanel() {
  const { preferences, setPreference, resetPreferences } = useInterfacePreferences();
  return /* @__PURE__ */ jsxs2(Box, { sx: { bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 1.2, p: 1.35 }, children: [
    /* @__PURE__ */ jsx3(Typography, { sx: { fontSize: "0.88rem", fontWeight: 900 }, children: "Interface" }),
    /* @__PURE__ */ jsx3(Typography, { sx: { mt: 0.35, mb: 0.8, fontSize: "0.72rem", color: "text.secondary", lineHeight: 1.45 }, children: "Make Paddiq easier to read and comfortable to use on this device. Changes apply immediately." }),
    /* @__PURE__ */ jsxs2(Box, { sx: rowSx, children: [
      /* @__PURE__ */ jsx3(SettingLabel, { icon: /* @__PURE__ */ jsx3(SettingsBrightnessOutlinedIcon2, {}), title: "Color theme", description: "Use light, dark, or your device setting." }),
      /* @__PURE__ */ jsx3(ThemeModeSelect, { sx: { justifySelf: { sm: "end" } } })
    ] }),
    /* @__PURE__ */ jsx3(Divider, {}),
    /* @__PURE__ */ jsxs2(Box, { sx: rowSx, children: [
      /* @__PURE__ */ jsx3(SettingLabel, { icon: /* @__PURE__ */ jsx3(TextFieldsRoundedIcon, {}), title: "Text size", description: "Increase labels, tables, and controls throughout the app." }),
      /* @__PURE__ */ jsx3(ChoiceControl, { preferenceKey: "textSize", options: [
        { value: "default", label: "Default" },
        { value: "large", label: "Large" },
        { value: "extra-large", label: "Extra large" }
      ] })
    ] }),
    /* @__PURE__ */ jsx3(Divider, {}),
    /* @__PURE__ */ jsxs2(Box, { sx: rowSx, children: [
      /* @__PURE__ */ jsx3(SettingLabel, { icon: /* @__PURE__ */ jsx3(DensitySmallOutlinedIcon, {}), title: "Control spacing", description: "Choose a tight layout or larger, easier targets." }),
      /* @__PURE__ */ jsx3(ChoiceControl, { preferenceKey: "density", options: [
        { value: "compact", label: "Compact" },
        { value: "comfortable", label: "Comfortable" }
      ] })
    ] }),
    /* @__PURE__ */ jsx3(Divider, {}),
    /* @__PURE__ */ jsxs2(Box, { sx: rowSx, children: [
      /* @__PURE__ */ jsx3(SettingLabel, { icon: /* @__PURE__ */ jsx3(ViewSidebarOutlinedIcon, {}), title: "Sidebar on startup", description: "Remember its last state or always start open or collapsed." }),
      /* @__PURE__ */ jsx3(ChoiceControl, { preferenceKey: "sidebarDefault", options: [
        { value: "remember", label: "Remember" },
        { value: "open", label: "Open" },
        { value: "collapsed", label: "Collapsed" }
      ] })
    ] }),
    /* @__PURE__ */ jsx3(Divider, {}),
    /* @__PURE__ */ jsxs2(Box, { sx: rowSx, children: [
      /* @__PURE__ */ jsx3(SettingLabel, { icon: /* @__PURE__ */ jsx3(MotionPhotosOffOutlinedIcon, {}), title: "Reduce motion", description: "Minimize animation and interface movement." }),
      /* @__PURE__ */ jsx3(Switch, { checked: preferences.reduceMotion, onChange: (event) => setPreference("reduceMotion", event.target.checked), inputProps: { "aria-label": "Reduce motion" }, sx: { justifySelf: { sm: "end" } } })
    ] }),
    /* @__PURE__ */ jsx3(Divider, {}),
    /* @__PURE__ */ jsxs2(Box, { sx: rowSx, children: [
      /* @__PURE__ */ jsx3(SettingLabel, { icon: /* @__PURE__ */ jsx3(ContrastOutlinedIcon, {}), title: "Higher contrast", description: "Strengthen borders and secondary text." }),
      /* @__PURE__ */ jsx3(Switch, { checked: preferences.highContrast, onChange: (event) => setPreference("highContrast", event.target.checked), inputProps: { "aria-label": "Higher contrast" }, sx: { justifySelf: { sm: "end" } } })
    ] }),
    /* @__PURE__ */ jsx3(
      Button,
      {
        variant: "text",
        size: "small",
        startIcon: /* @__PURE__ */ jsx3(RestartAltOutlinedIcon, { sx: { fontSize: 15 } }),
        onClick: resetPreferences,
        sx: { mt: 0.6, px: 0.5, color: "text.secondary", fontSize: "0.7rem", textTransform: "none" },
        children: "Reset interface settings"
      }
    )
  ] });
}

// src/components/layout/navSelection.ts
function navDestinationIsSelected(currentPath, destination, activePaths = []) {
  const [currentPathname, currentQuery = ""] = currentPath.split("?");
  if (activePaths.some((path) => currentPathname.startsWith(path))) return true;
  const [destinationPathname, destinationQuery = ""] = destination.split("?");
  if (currentPathname !== destinationPathname) return false;
  const destinationParams = new URLSearchParams(destinationQuery);
  const currentParams = new URLSearchParams(currentQuery);
  if (destinationParams.get("view") === "all" && !currentParams.has("view")) return true;
  return [...destinationParams.entries()].every(([key, value]) => currentParams.get(key) === value);
}
function currentNavMenu(items, selected) {
  return items.find((item) => item.path === selected?.path) ?? null;
}

// src/components/layout/AppShell.tsx
import React2, { useEffect as useEffect2, useId, useRef, useState as useState2 } from "react";
import {
  Badge,
  Box as Box3,
  Button as Button2,
  Drawer,
  IconButton as IconButton2,
  InputBase,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  ClickAwayListener,
  Typography as Typography2,
  Divider as Divider2,
  Avatar,
  Tooltip,
  useMediaQuery,
  useTheme as useTheme2
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import DisplaySettingsOutlinedIcon from "@mui/icons-material/DisplaySettingsOutlined";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";

// src/components/brand/PaddiqLogo.tsx
import { Box as Box2, useTheme } from "@mui/material";
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var ICON_ASPECT = 341 / 306;
var ACCENT = "#9A28FE";
var WORDMARK = "PADDIQ";
var scales = {
  brand: {
    iconHeight: 64,
    wordmarkSize: "2.625rem",
    taglineSize: "0.72rem",
    taglineTracking: "0.1em",
    wordmarkTracking: "0.08em",
    gap: 1.5,
    textGap: 0.45,
    showTagline: true
  },
  header: {
    iconHeight: 28,
    wordmarkSize: "0.8125rem",
    taglineSize: "0.72rem",
    taglineTracking: "0.12em",
    wordmarkTracking: "0.12em",
    gap: 1,
    textGap: 0,
    showTagline: false
  },
  sidebar: {
    iconHeight: 24,
    compactIconHeight: 20,
    wordmarkSize: "0.875rem",
    taglineSize: "0.72rem",
    taglineTracking: "0.12em",
    wordmarkTracking: "0.08em",
    gap: 0.75,
    textGap: 0,
    showTagline: false,
    matchIconHeight: true
  }
};
function WordmarkLetters({
  fontSize,
  letterSpacing,
  matchIconHeight = false,
  paddiColor
}) {
  const wordmarkStyle = {
    display: "inline-block",
    fontWeight: 900,
    fontStyle: "italic",
    fontSize,
    lineHeight: matchIconHeight ? 1 : 1.05,
    letterSpacing,
    whiteSpace: "nowrap"
  };
  return /* @__PURE__ */ jsxs3(Box2, { component: "span", "aria-label": WORDMARK, sx: wordmarkStyle, children: [
    /* @__PURE__ */ jsx4(Box2, { component: "span", "aria-hidden": true, sx: { color: paddiColor, display: "inline" }, children: "PADDI" }),
    /* @__PURE__ */ jsx4(Box2, { component: "span", "aria-hidden": true, sx: { color: ACCENT, display: "inline", letterSpacing: 0 }, children: "Q" })
  ] });
}
function PaddiqLogo({
  variant = "brand",
  iconSrc = "/ICON.png",
  compact = false,
  wordmarkColor,
  wordmarkSize,
  sx
}) {
  const theme2 = useTheme();
  const isDark = theme2.palette.mode === "dark";
  const paddiColor = wordmarkColor ?? (variant === "brand" || isDark ? "#fff" : "#1F2937");
  const scale = scales[variant];
  const iconHeight = compact && "compactIconHeight" in scale && scale.compactIconHeight ? scale.compactIconHeight : scale.iconHeight;
  const iconWidth = iconHeight * ICON_ASPECT;
  const matchIconHeight = "matchIconHeight" in scale && scale.matchIconHeight;
  const wordmarkFontSize = wordmarkSize ?? (matchIconHeight ? `${iconHeight}px` : scale.wordmarkSize);
  if (compact) {
    return /* @__PURE__ */ jsx4(
      Box2,
      {
        sx: {
          display: "inline-flex",
          alignItems: "center",
          ...sx
        },
        children: /* @__PURE__ */ jsx4(
          Box2,
          {
            component: "img",
            src: iconSrc,
            alt: "",
            "aria-hidden": true,
            sx: {
              width: iconWidth,
              height: iconHeight,
              objectFit: "contain",
              flexShrink: 0,
              display: "block"
            }
          }
        )
      }
    );
  }
  return /* @__PURE__ */ jsxs3(
    Box2,
    {
      sx: {
        display: "inline-flex",
        alignItems: "center",
        gap: scale.gap,
        ...sx
      },
      children: [
        /* @__PURE__ */ jsx4(
          Box2,
          {
            component: "img",
            src: iconSrc,
            alt: "",
            "aria-hidden": true,
            sx: {
              width: iconWidth,
              height: iconHeight,
              objectFit: "contain",
              flexShrink: 0,
              display: "block"
            }
          }
        ),
        /* @__PURE__ */ jsxs3(
          Box2,
          {
            sx: {
              display: "inline-grid",
              gridTemplateColumns: "1fr",
              justifyContent: "center"
            },
            children: [
              scale.showTagline ? /* @__PURE__ */ jsx4(
                Box2,
                {
                  "aria-hidden": true,
                  sx: {
                    gridArea: "1 / 1",
                    visibility: "hidden",
                    fontWeight: 600,
                    fontStyle: "italic",
                    fontSize: scale.taglineSize,
                    letterSpacing: scale.taglineTracking,
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                    userSelect: "none"
                  },
                  children: "TRACK DATA. REAL INSIGHT. EVERY LAP."
                }
              ) : null,
              /* @__PURE__ */ jsxs3(
                Box2,
                {
                  sx: {
                    gridArea: "1 / 1",
                    display: "flex",
                    flexDirection: "column",
                    gap: scale.textGap,
                    justifyContent: "center",
                    width: "100%"
                  },
                  children: [
                    /* @__PURE__ */ jsx4(
                      WordmarkLetters,
                      {
                        fontSize: wordmarkFontSize,
                        letterSpacing: scale.wordmarkTracking,
                        matchIconHeight: Boolean(matchIconHeight),
                        paddiColor
                      }
                    ),
                    scale.showTagline ? /* @__PURE__ */ jsxs3(
                      Box2,
                      {
                        component: "span",
                        sx: {
                          fontWeight: 600,
                          fontStyle: "italic",
                          fontSize: scale.taglineSize,
                          lineHeight: 1.35,
                          letterSpacing: scale.taglineTracking,
                          textTransform: "uppercase",
                          whiteSpace: "nowrap"
                        },
                        children: [
                          /* @__PURE__ */ jsxs3(Box2, { component: "span", sx: { color: "#fff" }, children: [
                            "TRACK DATA. REAL INSIGHT. EVERY",
                            " "
                          ] }),
                          /* @__PURE__ */ jsx4(Box2, { component: "span", sx: { color: ACCENT }, children: "LAP." })
                        ]
                      }
                    ) : null
                  ]
                }
              )
            ]
          }
        )
      ]
    }
  );
}

// src/components/layout/AppShell.tsx
import { Fragment, jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
var SIDEBAR_WIDTH = 196;
var SIDEBAR_COLLAPSED_WIDTH = 52;
var SIDEBAR_COLLAPSED_STORAGE_KEY = "paddiq:sidebar:collapsed";
function loadSidebarCollapsedPreference(key = SIDEBAR_COLLAPSED_STORAGE_KEY, fallback = true) {
  if (typeof window === "undefined") {
    return fallback;
  }
  try {
    const stored = window.localStorage.getItem(key);
    return stored === null ? fallback : stored === "true";
  } catch {
    return fallback;
  }
}
function persistSidebarCollapsedPreference(collapsed, key = SIDEBAR_COLLAPSED_STORAGE_KEY) {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(key, String(collapsed));
  } catch {
  }
}
function AppShell({
  navItems,
  currentPath,
  onNavigate,
  teams = [],
  currentTeamId,
  onTeamChange,
  userName,
  userRole,
  userInitials,
  sidebarDriver,
  onUserAction,
  userActionIcon,
  userActionLabel = "Open user settings",
  userMenuItems,
  onUserMenuSelect,
  onLogout,
  logoutLabel = "Log out",
  onFeedback,
  feedbackIcon,
  feedbackLabel = "Feedback",
  onHelp,
  onSettings,
  headerActions,
  controlsPlacement = "header",
  showNavigationSearch = true,
  globalSearchResults = [],
  globalSearchLoading = false,
  globalSearchError,
  onGlobalSearchQueryChange,
  appTitle = "PADDIQ",
  brandIconSrc = "/ICON.png",
  viewportHeight = "100dvh",
  showSidebarCollapseControl = true,
  sidebarToggleTrigger,
  onSidebarCollapsedChange,
  sidebarPreferenceKey,
  defaultSidebarCollapsed = true,
  mainContentRef,
  children
}) {
  const { preferences: interfacePreferences } = useInterfacePreferences();
  const searchResultsId = useId();
  const headerSearchRef = useRef(null);
  const muiTheme = useTheme2();
  const isDark = muiTheme.palette.mode === "dark";
  const chrome = getSidebarChrome(isDark);
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState2(false);
  const [collapsedPref, setCollapsedPref] = useState2(() => {
    if (sidebarPreferenceKey) return loadSidebarCollapsedPreference(sidebarPreferenceKey, defaultSidebarCollapsed);
    if (interfacePreferences.sidebarDefault === "open") return false;
    if (interfacePreferences.sidebarDefault === "collapsed") return true;
    return loadSidebarCollapsedPreference();
  });
  const collapsed = collapsedPref && !isMobile;
  const [navQuery, setNavQuery] = useState2("");
  const [headerQuery, setHeaderQuery] = useState2("");
  const [openNavGroups, setOpenNavGroups] = useState2({});
  const [collapsedNavMenuAnchor, setCollapsedNavMenuAnchor] = useState2(null);
  const [collapsedNavMenuItem, setCollapsedNavMenuItem] = useState2(null);
  const activeNavMenuItem = currentNavMenu(navItems, collapsedNavMenuItem);
  const [headerSearchAnchor, setHeaderSearchAnchor] = useState2(null);
  const searchInputRef = useRef(null);
  const sidebarToggleTriggerRef = useRef(sidebarToggleTrigger);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState2(null);
  const accountMenuOpen = Boolean(accountMenuAnchor);
  const defaultAccountMenuItems = userMenuItems ?? [
    { id: "profile", label: "Profile & avatar" },
    { id: "interface", label: "Interface" },
    { id: "measurements", label: "Measurements" },
    { id: "password", label: "Change password" }
  ];
  useEffect2(() => {
    if (sidebarPreferenceKey) return;
    if (interfacePreferences.sidebarDefault === "open") {
      setCollapsedPref(false);
    } else if (interfacePreferences.sidebarDefault === "collapsed") {
      setCollapsedPref(true);
    }
  }, [interfacePreferences.sidebarDefault, sidebarPreferenceKey]);
  useEffect2(() => {
    if (sidebarToggleTrigger === void 0 || sidebarToggleTriggerRef.current === sidebarToggleTrigger) {
      return;
    }
    sidebarToggleTriggerRef.current = sidebarToggleTrigger;
    setCollapsedNavMenuAnchor(null);
    setCollapsedNavMenuItem(null);
    setCollapsedPref((value) => {
      const next = !value;
      persistSidebarCollapsedPreference(next, sidebarPreferenceKey);
      return next;
    });
  }, [sidebarToggleTrigger, sidebarPreferenceKey]);
  useEffect2(() => {
    onSidebarCollapsedChange?.(collapsed);
  }, [collapsed, onSidebarCollapsedChange]);
  function openAccountMenu(event) {
    setAccountMenuAnchor(event.currentTarget);
  }
  function closeAccountMenu() {
    setAccountMenuAnchor(null);
  }
  function handleAccountMenuSelect(itemId) {
    closeAccountMenu();
    if (onUserMenuSelect) {
      onUserMenuSelect(itemId);
      return;
    }
    onNavigate(`${profilePath}?section=${itemId}`);
  }
  function handleLogoutClick() {
    closeAccountMenu();
    onLogout?.();
  }
  function openInterfaceSettings() {
    if (onSettings) {
      onSettings();
    } else if (onUserAction) {
      onUserAction();
    } else {
      handleAccountMenuSelect("interface");
    }
  }
  function navigateFromSearch(path, external) {
    setHeaderSearchAnchor(null);
    setHeaderQuery("");
    onGlobalSearchQueryChange?.("");
    if (external) {
      window.open(path, "_blank", "noopener,noreferrer");
      return;
    }
    onNavigate(path);
    if (isMobile) setMobileOpen(false);
  }
  function toggleSidebarCollapsed() {
    setCollapsedNavMenuAnchor(null);
    setCollapsedNavMenuItem(null);
    setCollapsedPref((value) => {
      const next = !value;
      persistSidebarCollapsedPreference(next, sidebarPreferenceKey);
      return next;
    });
  }
  function closeCollapsedNavMenu() {
    setCollapsedNavMenuAnchor(null);
    setCollapsedNavMenuItem(null);
  }
  function expandSidebarForSearch() {
    setCollapsedPref(false);
    persistSidebarCollapsedPreference(false, sidebarPreferenceKey);
    window.setTimeout(() => searchInputRef.current?.focus(), 220);
  }
  useEffect2(() => {
    function focusNavigationSearch(event) {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      const target = event.target;
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
        return;
      }
      event.preventDefault();
      if (!showNavigationSearch) {
        headerSearchRef.current?.querySelector("input")?.focus();
        return;
      }
      if (collapsed) {
        setCollapsedPref(false);
        persistSidebarCollapsedPreference(false, sidebarPreferenceKey);
        window.setTimeout(() => searchInputRef.current?.focus(), 220);
        return;
      }
      searchInputRef.current?.focus();
    }
    window.addEventListener("keydown", focusNavigationSearch);
    return () => window.removeEventListener("keydown", focusNavigationSearch);
  }, [collapsed, showNavigationSearch]);
  function accountMenuIcon(itemId) {
    switch (itemId) {
      case "account":
      case "profile":
        return /* @__PURE__ */ jsx5(PersonOutlineOutlinedIcon, { sx: { fontSize: 16 } });
      case "measurements":
        return /* @__PURE__ */ jsx5(StraightenOutlinedIcon, { sx: { fontSize: 16 } });
      case "interface":
        return /* @__PURE__ */ jsx5(DisplaySettingsOutlinedIcon, { sx: { fontSize: 16 } });
      case "password":
        return /* @__PURE__ */ jsx5(LockOutlinedIcon, { sx: { fontSize: 16 } });
      default:
        return null;
    }
  }
  const sidebarW = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;
  const selectedTeam = teams.find((team) => team.id === currentTeamId) ?? teams[0];
  const normalizedNavQuery = navQuery.trim().toLowerCase();
  const normalizedHeaderQuery = headerQuery.trim().toLowerCase();
  const visibleNavItems = normalizedNavQuery ? navItems.filter((item) => item.label.toLowerCase().includes(normalizedNavQuery) || item.children?.some((child) => child.label.toLowerCase().includes(normalizedNavQuery))) : navItems;
  const pageSearchResults = normalizedHeaderQuery ? navItems.flatMap((item) => [
    { id: `page:${item.path}`, group: "Pages", label: item.label, path: item.path, icon: item.icon, external: item.external },
    ...(item.children ?? []).map((child) => ({
      id: `page:${child.path}`,
      group: "Pages",
      label: child.label,
      path: child.path,
      icon: child.icon ?? item.icon,
      external: false
    }))
  ]).filter((item) => item.label.toLowerCase().includes(normalizedHeaderQuery)).slice(0, 5) : [];
  const headerSearchResults = [...globalSearchResults, ...pageSearchResults].filter((result, index, results) => results.findIndex((candidate) => candidate.path === result.path && candidate.label === result.label) === index).slice(0, 20);
  const activeDriver = sidebarDriver ?? {
    name: userName,
    role: userRole,
    initials: userInitials,
    profilePath: "/settings"
  };
  const profilePath = activeDriver.profilePath ?? "/settings";
  const showAccountMenu = Boolean(onUserMenuSelect || onLogout || profilePath === "/settings");
  const profileAvatar = activeDriver.avatar ? /* @__PURE__ */ jsx5(Box3, { sx: { width: 28, height: 28, flexShrink: 0, "& .MuiAvatar-root": { width: "100%", height: "100%", fontSize: "0.62rem" } }, children: activeDriver.avatar }) : /* @__PURE__ */ jsx5(Avatar, { src: activeDriver.avatarUrl ?? void 0, sx: { width: 28, height: 28, fontSize: "0.62rem", fontWeight: 900, flexShrink: 0, bgcolor: chrome.profileAvatarBg, color: "#FFFFFF", border: `1px solid ${chrome.profileAvatarBorder}` }, children: activeDriver.initials });
  const accountControls = /* @__PURE__ */ jsxs4(Box3, { sx: { ml: controlsPlacement === "sidebar" ? 0 : "auto", display: "flex", alignItems: "center", flexDirection: controlsPlacement === "sidebar" && collapsed ? "column" : "row", gap: 0.25, flexShrink: 0, width: controlsPlacement === "sidebar" ? "100%" : void 0 }, children: [
    headerActions,
    onHelp ? /* @__PURE__ */ jsx5(Tooltip, { title: "Help & guides", children: /* @__PURE__ */ jsx5(IconButton2, { onClick: onHelp, "aria-label": "Help & guides", sx: { width: 32, height: 32, borderRadius: "7px", color: chrome.textSecondary }, children: /* @__PURE__ */ jsx5(HelpOutlineRoundedIcon, { sx: { fontSize: 18 } }) }) }) : null,
    onFeedback ? /* @__PURE__ */ jsx5(Tooltip, { title: feedbackLabel, children: /* @__PURE__ */ jsx5(IconButton2, { onClick: onFeedback, "aria-label": feedbackLabel, sx: { width: 32, height: 32, borderRadius: "7px", color: chrome.textSecondary, "& svg": { fontSize: 18 } }, children: feedbackIcon ?? /* @__PURE__ */ jsx5(MoreHorizIcon, {}) }) }) : null,
    onSettings || onUserAction || showAccountMenu ? /* @__PURE__ */ jsx5(Tooltip, { title: onSettings ? "Interface settings" : userActionLabel, children: /* @__PURE__ */ jsx5(
      IconButton2,
      {
        onClick: openInterfaceSettings,
        "aria-label": "Interface settings",
        sx: { width: 32, height: 32, borderRadius: "7px", color: chrome.textSecondary },
        children: userActionIcon ?? /* @__PURE__ */ jsx5(DisplaySettingsOutlinedIcon, { sx: { fontSize: 18 } })
      }
    ) }) : null,
    /* @__PURE__ */ jsxs4(
      Button2,
      {
        onClick: showAccountMenu ? openAccountMenu : () => onNavigate(profilePath),
        "aria-label": showAccountMenu ? "Open profile menu" : "View profile",
        sx: { minWidth: 0, height: 36, ml: controlsPlacement === "sidebar" ? 0 : 0.25, px: controlsPlacement === "sidebar" && collapsed ? 0 : { xs: 0.4, sm: 0.65 }, gap: 0.7, borderRadius: "8px", color: chrome.text, textTransform: "none" },
        children: [
          profileAvatar,
          /* @__PURE__ */ jsx5(Typography2, { noWrap: true, sx: { display: controlsPlacement === "sidebar" ? collapsed ? "none" : "block" : { xs: "none", lg: "block" }, maxWidth: controlsPlacement === "sidebar" ? 66 : 140, fontSize: "0.72rem", fontWeight: 750 }, children: activeDriver.name }),
          showAccountMenu ? /* @__PURE__ */ jsx5(KeyboardArrowDownRoundedIcon, { sx: { display: controlsPlacement === "sidebar" ? "none" : { xs: "none", sm: "block" }, fontSize: 15, color: chrome.textMuted } }) : null
        ]
      }
    )
  ] });
  const sidebarContent = /* @__PURE__ */ jsxs4(
    Box3,
    {
      sx: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
        maxHeight: viewportHeight,
        minHeight: 0,
        width: sidebarW,
        transition: "width 0.2s ease",
        overflow: "hidden",
        color: chrome.text
      },
      children: [
        /* @__PURE__ */ jsx5(
          Box3,
          {
            "aria-label": appTitle,
            sx: {
              display: "flex",
              alignItems: "center",
              px: collapsed ? 0.75 : "12px",
              py: "9px",
              minHeight: 50,
              flexShrink: 0,
              justifyContent: collapsed ? "center" : "flex-start"
            },
            children: collapsed && showSidebarCollapseControl ? /* @__PURE__ */ jsx5(Tooltip, { title: "Expand navigation", placement: "right", children: /* @__PURE__ */ jsx5(
              IconButton2,
              {
                onClick: toggleSidebarCollapsed,
                "aria-label": "Expand navigation",
                sx: { width: 36, height: 36, borderRadius: "7px" },
                children: /* @__PURE__ */ jsx5(PaddiqLogo, { variant: "sidebar", compact: true, iconSrc: brandIconSrc })
              }
            ) }) : collapsed ? /* @__PURE__ */ jsx5(PaddiqLogo, { variant: "sidebar", compact: true, iconSrc: brandIconSrc }) : /* @__PURE__ */ jsxs4(Fragment, { children: [
              /* @__PURE__ */ jsxs4(
                Box3,
                {
                  component: "button",
                  type: "button",
                  disabled: !selectedTeam || !onTeamChange,
                  onClick: selectedTeam && onTeamChange ? () => onTeamChange(selectedTeam.id) : void 0,
                  sx: {
                    display: "flex",
                    alignItems: "center",
                    gap: "7px",
                    minWidth: 0,
                    flex: 1,
                    cursor: selectedTeam && onTeamChange ? "pointer" : "default",
                    p: 0,
                    border: 0,
                    bgcolor: "transparent",
                    textAlign: "left"
                  },
                  children: [
                    /* @__PURE__ */ jsx5(PaddiqLogo, { variant: "sidebar", compact: true, iconSrc: brandIconSrc }),
                    /* @__PURE__ */ jsx5(Typography2, { sx: { fontSize: "0.75rem", fontWeight: 650, color: chrome.text }, noWrap: true, children: selectedTeam?.name ?? appTitle }),
                    selectedTeam && onTeamChange ? /* @__PURE__ */ jsx5(MoreHorizIcon, { sx: { fontSize: 14, color: chrome.textMuted } }) : null
                  ]
                }
              ),
              !isMobile && showSidebarCollapseControl ? /* @__PURE__ */ jsx5(Tooltip, { title: "Collapse navigation", placement: "right", children: /* @__PURE__ */ jsx5(
                IconButton2,
                {
                  onClick: toggleSidebarCollapsed,
                  "aria-label": "Collapse navigation",
                  sx: { width: 28, height: 28, ml: 0.5, color: chrome.textMuted, borderRadius: "6px" },
                  children: /* @__PURE__ */ jsx5(ChevronLeftIcon, { sx: { fontSize: 17 } })
                }
              ) }) : null
            ] })
          }
        ),
        showNavigationSearch && (collapsed ? /* @__PURE__ */ jsx5(Tooltip, { title: "Search navigation", placement: "right", children: /* @__PURE__ */ jsx5(
          IconButton2,
          {
            onClick: expandSidebarForSearch,
            "aria-label": "Search navigation",
            sx: {
              width: 32,
              height: 32,
              mx: "10px",
              mb: "8px",
              color: chrome.textSecondary,
              borderRadius: "7px",
              border: `1px solid ${chrome.border}`,
              bgcolor: isDark ? "#111113" : "#FFFFFF"
            },
            children: /* @__PURE__ */ jsx5(SearchRoundedIcon, { sx: { fontSize: 17 } })
          }
        ) }) : /* @__PURE__ */ jsx5(Box3, { sx: { px: "8px", mb: "8px", flexShrink: 0 }, children: /* @__PURE__ */ jsxs4(
          Box3,
          {
            sx: {
              height: 32,
              display: "flex",
              alignItems: "center",
              gap: "7px",
              px: "7px",
              borderRadius: "7px",
              border: `1px solid ${chrome.border}`,
              bgcolor: isDark ? "#111113" : "#FFFFFF",
              color: chrome.textMuted,
              "&:focus-within": { borderColor: isDark ? "rgba(167,139,250,0.36)" : "#C4B5E9" }
            },
            children: [
              /* @__PURE__ */ jsx5(SearchRoundedIcon, { sx: { fontSize: 17, flexShrink: 0 } }),
              /* @__PURE__ */ jsx5(
                InputBase,
                {
                  inputRef: searchInputRef,
                  value: navQuery,
                  onChange: (event) => setNavQuery(event.target.value),
                  onKeyDown: (event) => {
                    if (event.key === "Escape") {
                      setNavQuery("");
                      event.currentTarget.blur();
                    }
                  },
                  placeholder: "Search navigation...",
                  inputProps: { "aria-label": "Search navigation" },
                  sx: {
                    minWidth: 0,
                    flex: 1,
                    color: chrome.text,
                    fontSize: "0.75rem",
                    "& input::placeholder": { color: chrome.textMuted, opacity: 1 }
                  }
                }
              ),
              /* @__PURE__ */ jsx5(
                Box3,
                {
                  "aria-hidden": true,
                  sx: {
                    minWidth: 18,
                    height: 18,
                    display: "grid",
                    placeItems: "center",
                    borderRadius: "4px",
                    border: `1px solid ${chrome.border}`,
                    color: chrome.textMuted,
                    fontSize: "0.625rem"
                  },
                  children: "/"
                }
              )
            ]
          }
        ) })),
        /* @__PURE__ */ jsxs4(
          List,
          {
            sx: {
              px: "8px",
              py: 0,
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" }
            },
            disablePadding: true,
            children: [
              visibleNavItems.map((item) => {
                const canNavigate = !item.disableNavigation;
                const currentPathname = currentPath.split("?")[0];
                const itemPathname = item.path.split("?")[0];
                const isSelected = !item.external && (item.path === "/" ? currentPath === "/" : item.children?.length ? currentPathname === itemPathname || currentPathname?.startsWith(`${itemPathname}/`) : currentPath.startsWith(item.path));
                const hasNotification = item.notificationCount !== void 0 && item.notificationCount > 0 || item.notificationPending;
                const hasChildren = Boolean(item.children?.length);
                const groupOpen = !collapsed && (normalizedNavQuery.length > 0 || openNavGroups[item.path] !== false && (openNavGroups[item.path] === true || isSelected));
                return /* @__PURE__ */ jsxs4(React2.Fragment, { children: [
                  item.dividerBefore && /* @__PURE__ */ jsx5(Box3, { sx: { height: 10 } }),
                  /* @__PURE__ */ jsx5(Tooltip, { title: collapsed ? item.tooltip ?? item.label : item.tooltip ?? "", placement: "right", children: /* @__PURE__ */ jsxs4(
                    ListItemButton,
                    {
                      selected: isSelected,
                      "aria-label": item.label,
                      "aria-expanded": hasChildren && !collapsed ? groupOpen : void 0,
                      "aria-haspopup": hasChildren && collapsed ? "menu" : void 0,
                      onClick: canNavigate ? (event) => {
                        if (hasChildren && collapsed) {
                          setCollapsedNavMenuAnchor(event.currentTarget);
                          setCollapsedNavMenuItem(item);
                          return;
                        }
                        if (item.external) {
                          window.open(item.path, "_blank", "noopener,noreferrer");
                        } else {
                          onNavigate(item.path);
                        }
                        if (hasChildren && !collapsed) {
                          setOpenNavGroups((groups) => ({ ...groups, [item.path]: !groupOpen }));
                        }
                        if (isMobile && !hasChildren) setMobileOpen(false);
                      } : void 0,
                      "aria-disabled": !canNavigate ? true : void 0,
                      tabIndex: canNavigate ? void 0 : -1,
                      disableRipple: !canNavigate,
                      sx: {
                        borderRadius: "6px",
                        mb: "2px",
                        py: 0,
                        px: collapsed ? 0.75 : "8px",
                        justifyContent: collapsed ? "center" : "flex-start",
                        minHeight: 34,
                        cursor: canNavigate ? "pointer" : "default",
                        color: isSelected ? chrome.navActiveText : chrome.navText,
                        "& .MuiListItemIcon-root": {
                          color: "inherit"
                        },
                        "&:hover": {
                          bgcolor: canNavigate ? chrome.navHover : "transparent"
                        },
                        "&.Mui-selected": {
                          bgcolor: chrome.navActiveBg,
                          color: chrome.navActiveText,
                          boxShadow: "none",
                          "& .MuiListItemIcon-root": {
                            color: chrome.navActiveIcon
                          },
                          "& .MuiListItemText-root, & .MuiListItemText-root .MuiTypography-root": {
                            color: chrome.navActiveText
                          }
                        },
                        "&.Mui-selected:hover": {
                          bgcolor: canNavigate ? chrome.navActiveHover : chrome.navActiveBg
                        }
                      },
                      children: [
                        /* @__PURE__ */ jsx5(
                          ListItemIcon,
                          {
                            sx: {
                              minWidth: collapsed ? "auto" : 27,
                              opacity: 1,
                              "& svg": { fontSize: 17, strokeWidth: 1.7 }
                            },
                            children: hasNotification && collapsed ? item.notificationCount !== void 0 && item.notificationCount > 0 ? /* @__PURE__ */ jsx5(
                              Badge,
                              {
                                badgeContent: item.notificationCount,
                                color: "info",
                                overlap: "circular",
                                sx: {
                                  "& .MuiBadge-badge": {
                                    fontSize: "0.62rem",
                                    height: 16,
                                    minWidth: 16,
                                    fontWeight: 700
                                  }
                                },
                                children: item.icon
                              }
                            ) : /* @__PURE__ */ jsx5(
                              Badge,
                              {
                                variant: "dot",
                                color: "info",
                                overlap: "circular",
                                sx: {
                                  "& .MuiBadge-badge": {
                                    height: 8,
                                    minWidth: 8,
                                    borderRadius: "50%"
                                  }
                                },
                                children: item.icon
                              }
                            ) : item.icon
                          }
                        ),
                        !collapsed && /* @__PURE__ */ jsxs4(Fragment, { children: [
                          /* @__PURE__ */ jsx5(
                            ListItemText,
                            {
                              primary: /* @__PURE__ */ jsx5(Typography2, { variant: "body2", fontWeight: isSelected ? 650 : 450, fontSize: "0.75rem", lineHeight: 1.25, children: item.label }),
                              sx: { my: 0 }
                            }
                          ),
                          item.notificationCount !== void 0 && item.notificationCount > 0 && /* @__PURE__ */ jsxs4(Box3, { sx: { display: "flex", alignItems: "center", gap: 0.5, ml: 0.5 }, children: [
                            /* @__PURE__ */ jsx5(NotificationsNoneOutlinedIcon, { sx: { fontSize: 18, opacity: 0.95 } }),
                            /* @__PURE__ */ jsx5(
                              Box3,
                              {
                                sx: {
                                  minWidth: 18,
                                  height: 18,
                                  borderRadius: "5px",
                                  bgcolor: "#38BDF8",
                                  color: "#0B1220",
                                  display: "grid",
                                  placeItems: "center",
                                  px: 0.5,
                                  fontSize: "0.72rem",
                                  fontWeight: 800
                                },
                                children: item.notificationCount
                              }
                            )
                          ] }),
                          item.notificationPending && !(item.notificationCount !== void 0 && item.notificationCount > 0) && /* @__PURE__ */ jsx5(NotificationsNoneOutlinedIcon, { sx: { fontSize: 18, opacity: 0.72, ml: 0.5 } }),
                          item.badge !== void 0 && !hasNotification && /* @__PURE__ */ jsx5(
                            Box3,
                            {
                              sx: {
                                minWidth: 18,
                                height: 18,
                                borderRadius: "5px",
                                bgcolor: isDark ? "#27272A" : "#E4E4E7",
                                color: chrome.textSecondary,
                                display: "grid",
                                placeItems: "center",
                                px: 0.5,
                                fontSize: "0.7rem",
                                fontWeight: 700
                              },
                              children: item.badge
                            }
                          ),
                          hasChildren ? groupOpen ? /* @__PURE__ */ jsx5(KeyboardArrowDownRoundedIcon, { sx: { ml: 0.5, fontSize: 15, color: chrome.textMuted } }) : /* @__PURE__ */ jsx5(KeyboardArrowRightRoundedIcon, { sx: { ml: 0.5, fontSize: 15, color: chrome.textMuted } }) : null
                        ] })
                      ]
                    }
                  ) }),
                  groupOpen ? /* @__PURE__ */ jsx5(Box3, { sx: { mb: "3px", ml: "27px", pl: "8px", borderLeft: `1px solid ${chrome.border}` }, children: item.children?.map((child) => {
                    const childSelected = navDestinationIsSelected(currentPath, child.path, child.activePaths);
                    return /* @__PURE__ */ jsxs4(
                      ListItemButton,
                      {
                        selected: childSelected,
                        component: "a",
                        href: child.path,
                        "aria-current": childSelected ? "page" : void 0,
                        onClick: (event) => {
                          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                          event.preventDefault();
                          onNavigate(child.path);
                          if (isMobile) setMobileOpen(false);
                        },
                        sx: {
                          minHeight: 28,
                          py: 0,
                          px: "8px",
                          mb: "1px",
                          borderRadius: "5px",
                          color: childSelected ? chrome.navActiveText : chrome.textSecondary,
                          "&:hover": { bgcolor: chrome.navHover, color: chrome.text },
                          "&.Mui-selected": {
                            bgcolor: "transparent",
                            color: chrome.navActiveText
                          },
                          "&.Mui-selected:hover": { bgcolor: chrome.navHover }
                        },
                        children: [
                          child.icon && /* @__PURE__ */ jsx5(ListItemIcon, { sx: { minWidth: 24, color: "inherit" }, children: child.icon }),
                          /* @__PURE__ */ jsx5(
                            ListItemText,
                            {
                              primary: child.label,
                              primaryTypographyProps: {
                                fontSize: "0.7rem",
                                fontWeight: childSelected ? 650 : 450,
                                lineHeight: 1.2,
                                noWrap: true
                              },
                              sx: { my: 0 }
                            }
                          )
                        ]
                      },
                      child.path
                    );
                  }) }) : null
                ] }, item.path);
              }),
              !collapsed && visibleNavItems.length === 0 ? /* @__PURE__ */ jsx5(Typography2, { sx: { px: "9px", py: 1, fontSize: "0.75rem", color: chrome.textMuted }, children: "No navigation matches" }) : null
            ]
          }
        ),
        controlsPlacement === "sidebar" && /* @__PURE__ */ jsx5(Box3, { "data-testid": "sidebar-account-controls", sx: { mt: "auto", px: 1, py: 1, borderTop: `1px solid ${chrome.border}`, flexShrink: 0 }, children: accountControls }),
        /* @__PURE__ */ jsxs4(
          Menu,
          {
            anchorEl: collapsedNavMenuAnchor,
            open: Boolean(collapsedNavMenuAnchor && activeNavMenuItem),
            onClose: closeCollapsedNavMenu,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            transformOrigin: { vertical: "top", horizontal: "left" },
            slotProps: {
              paper: {
                elevation: 4,
                sx: {
                  ml: 0.75,
                  minWidth: 164,
                  p: 0.5,
                  border: `1px solid ${chrome.border}`,
                  borderRadius: "8px",
                  bgcolor: isDark ? "#111113" : "#FFFFFF"
                }
              },
              list: { "aria-label": activeNavMenuItem?.label, sx: { py: 0 } }
            },
            children: [
              /* @__PURE__ */ jsx5(Typography2, { sx: { px: 1, py: 0.65, fontSize: "0.66rem", fontWeight: 800, color: chrome.textMuted }, children: activeNavMenuItem?.label }),
              activeNavMenuItem?.children?.map((child) => {
                const childSelected = navDestinationIsSelected(currentPath, child.path, child.activePaths);
                return /* @__PURE__ */ jsxs4(
                  MenuItem,
                  {
                    selected: childSelected,
                    component: "a",
                    href: child.path,
                    "aria-current": childSelected ? "page" : void 0,
                    onClick: (event) => {
                      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                      event.preventDefault();
                      closeCollapsedNavMenu();
                      onNavigate(child.path);
                      if (isMobile) setMobileOpen(false);
                    },
                    sx: { minHeight: 32, px: 1, borderRadius: "5px", fontSize: "0.72rem", fontWeight: childSelected ? 700 : 500 },
                    children: [
                      child.icon && /* @__PURE__ */ jsx5(ListItemIcon, { sx: { minWidth: 24, color: "inherit" }, children: child.icon }),
                      child.label
                    ]
                  },
                  child.path
                );
              })
            ]
          }
        )
      ]
    }
  );
  return /* @__PURE__ */ jsxs4(Box3, { sx: { display: "flex", height: viewportHeight, overflow: "hidden", bgcolor: "background.default" }, children: [
    !isMobile && /* @__PURE__ */ jsx5(
      Box3,
      {
        component: "nav",
        sx: {
          width: sidebarW,
          flexShrink: 0,
          height: viewportHeight,
          transition: "width 0.2s ease",
          borderRight: `1px solid ${isDark ? chrome.border : colors.light.border}`,
          background: isDark ? colors.dark.sidebar : colors.light.sidebar,
          overflow: "hidden"
        },
        children: sidebarContent
      }
    ),
    isMobile && /* @__PURE__ */ jsx5(
      Drawer,
      {
        variant: "temporary",
        open: mobileOpen,
        onClose: () => setMobileOpen(false),
        ModalProps: { keepMounted: true },
        sx: {
          "& .MuiDrawer-paper": {
            width: SIDEBAR_WIDTH,
            bgcolor: isDark ? colors.dark.sidebar : colors.light.sidebar,
            background: isDark ? colors.dark.sidebar : colors.light.sidebar
          }
        },
        children: sidebarContent
      }
    ),
    /* @__PURE__ */ jsxs4(Box3, { sx: { flex: 1, display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0, overflow: "hidden" }, children: [
      /* @__PURE__ */ jsxs4(
        Box3,
        {
          component: "header",
          sx: {
            height: 50,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.75, md: 1.25 },
            px: { xs: 1, md: 1.5 },
            borderBottom: `1px solid ${isDark ? colors.dark.border : colors.light.border}`,
            bgcolor: isDark ? colors.dark.sidebar : "#FFFFFF"
          },
          children: [
            isMobile ? /* @__PURE__ */ jsxs4(Fragment, { children: [
              /* @__PURE__ */ jsx5(
                IconButton2,
                {
                  size: "small",
                  "aria-label": "Open navigation",
                  onClick: () => setMobileOpen(true),
                  sx: { width: 32, height: 32, borderRadius: "7px" },
                  children: /* @__PURE__ */ jsx5(MenuIcon, { sx: { fontSize: 19 } })
                }
              ),
              /* @__PURE__ */ jsx5(Box3, { sx: { display: { xs: "none", sm: "block" }, flexShrink: 0 }, children: /* @__PURE__ */ jsx5(PaddiqLogo, { variant: "sidebar", compact: true, iconSrc: brandIconSrc }) })
            ] }) : null,
            /* @__PURE__ */ jsxs4(
              Box3,
              {
                ref: headerSearchRef,
                onFocus: (event) => setHeaderSearchAnchor(event.currentTarget),
                sx: {
                  width: { xs: "auto", sm: 320, lg: 400 },
                  minWidth: 0,
                  height: 32,
                  flex: { xs: 1, sm: "0 1 auto" },
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1,
                  borderRadius: "7px",
                  border: `1px solid ${chrome.border}`,
                  bgcolor: isDark ? "#111113" : "#F8F8FA",
                  color: chrome.textMuted,
                  "&:focus-within": { borderColor: isDark ? "#52525B" : "#A1A1AA", bgcolor: isDark ? "#141416" : "#FFFFFF" }
                },
                children: [
                  /* @__PURE__ */ jsx5(SearchRoundedIcon, { sx: { fontSize: 17, flexShrink: 0 } }),
                  /* @__PURE__ */ jsx5(
                    InputBase,
                    {
                      value: headerQuery,
                      onChange: (event) => {
                        const query = event.target.value;
                        setHeaderQuery(query);
                        setHeaderSearchAnchor(headerSearchRef.current);
                        onGlobalSearchQueryChange?.(query);
                      },
                      onKeyDown: (event) => {
                        if (event.key === "Enter" && headerSearchResults[0]) {
                          navigateFromSearch(headerSearchResults[0].path, headerSearchResults[0].external);
                        }
                        if (event.key === "ArrowDown") {
                          event.preventDefault();
                          document.getElementById(searchResultsId)?.querySelector('[role="menuitem"]')?.focus();
                        }
                        if (event.key === "Escape") {
                          setHeaderQuery("");
                          onGlobalSearchQueryChange?.("");
                          setHeaderSearchAnchor(null);
                          event.currentTarget.blur();
                        }
                      },
                      placeholder: "Search Paddiq\u2026",
                      inputProps: {
                        "aria-label": "Search all of Paddiq",
                        "aria-haspopup": "menu",
                        "aria-expanded": Boolean(headerSearchAnchor && normalizedHeaderQuery),
                        "aria-controls": headerSearchAnchor && normalizedHeaderQuery ? searchResultsId : void 0
                      },
                      sx: { minWidth: 0, flex: 1, color: chrome.text, fontSize: "0.75rem", "& input::placeholder": { color: chrome.textMuted, opacity: 1 } }
                    }
                  ),
                  /* @__PURE__ */ jsx5(Typography2, { "aria-hidden": true, sx: { display: { xs: "none", md: "block" }, fontSize: "0.6rem", color: chrome.textMuted }, children: "/" })
                ]
              }
            ),
            controlsPlacement === "header" ? accountControls : null
          ]
        }
      ),
      /* @__PURE__ */ jsx5(
        Popper,
        {
          anchorEl: headerSearchAnchor,
          open: Boolean(headerSearchAnchor && normalizedHeaderQuery),
          placement: "bottom-start",
          sx: { zIndex: (theme2) => theme2.zIndex.modal },
          children: /* @__PURE__ */ jsx5(ClickAwayListener, { onClickAway: (event) => {
            if (event.target instanceof Node && !headerSearchAnchor?.contains(event.target)) setHeaderSearchAnchor(null);
          }, children: /* @__PURE__ */ jsx5(Paper, { elevation: 4, sx: { mt: 0.5, width: { xs: 280, sm: 400 }, maxWidth: "calc(100vw - 24px)", maxHeight: "min(600px, calc(100dvh - 80px))", overflowY: "auto", p: 0.5, border: `1px solid ${chrome.border}`, borderRadius: "8px", bgcolor: chrome.menuBg }, children: /* @__PURE__ */ jsx5(MenuList, { id: searchResultsId, "aria-label": "Search results", sx: { py: 0 }, onKeyDown: (event) => {
            if (event.key === "Escape") {
              setHeaderSearchAnchor(null);
              setHeaderQuery("");
              onGlobalSearchQueryChange?.("");
              headerSearchAnchor?.querySelector("input")?.focus();
            }
            if (event.key === "Tab") setHeaderSearchAnchor(null);
          }, children: globalSearchError ? /* @__PURE__ */ jsx5(Typography2, { role: "status", sx: { px: 1, py: 0.75, fontSize: "0.72rem", color: chrome.textMuted }, children: globalSearchError }) : globalSearchLoading ? /* @__PURE__ */ jsx5(Typography2, { sx: { px: 1, py: 0.75, fontSize: "0.72rem", color: chrome.textMuted }, children: "Searching everything\u2026" }) : headerSearchResults.length ? headerSearchResults.map((result, index) => {
            const showGroup = index === 0 || headerSearchResults[index - 1]?.group !== result.group;
            return /* @__PURE__ */ jsxs4(React2.Fragment, { children: [
              showGroup ? /* @__PURE__ */ jsx5(Typography2, { sx: { px: 1, pt: index === 0 ? 0.5 : 1, pb: 0.3, fontSize: "0.61rem", fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: chrome.textMuted }, children: result.group }) : null,
              /* @__PURE__ */ jsxs4(MenuItem, { onClick: () => navigateFromSearch(result.path, result.external), sx: { minHeight: 40, px: 1, gap: 1, borderRadius: "6px" }, children: [
                /* @__PURE__ */ jsx5(ListItemIcon, { sx: { minWidth: 24, color: chrome.textSecondary, "& svg": { fontSize: 16 } }, children: result.icon }),
                /* @__PURE__ */ jsxs4(Box3, { sx: { minWidth: 0 }, children: [
                  /* @__PURE__ */ jsx5(Typography2, { noWrap: true, sx: { fontSize: "0.75rem", fontWeight: 650 }, children: result.label }),
                  result.description ? /* @__PURE__ */ jsx5(Typography2, { noWrap: true, sx: { fontSize: "0.65rem", color: chrome.textMuted }, children: result.description }) : null
                ] })
              ] })
            ] }, `${result.id}:${result.path}`);
          }) : /* @__PURE__ */ jsx5(Typography2, { sx: { px: 1, py: 0.75, fontSize: "0.72rem", color: chrome.textMuted }, children: normalizedHeaderQuery.length < 2 ? "Type at least 2 characters" : "No results found" }) }) }) })
        }
      ),
      showAccountMenu ? /* @__PURE__ */ jsxs4(
        Menu,
        {
          anchorEl: accountMenuAnchor,
          open: accountMenuOpen,
          onClose: closeAccountMenu,
          anchorOrigin: { vertical: controlsPlacement === "sidebar" ? "top" : "bottom", horizontal: "right" },
          transformOrigin: { vertical: controlsPlacement === "sidebar" ? "bottom" : "top", horizontal: "right" },
          slotProps: { paper: { sx: { minWidth: 220, mt: 0.5, borderRadius: "8px", border: `1px solid ${chrome.border}`, bgcolor: chrome.menuBg, color: chrome.menuText } } },
          children: [
            /* @__PURE__ */ jsxs4(Box3, { sx: { px: 1.5, py: 1.15, borderBottom: `1px solid ${chrome.border}` }, onClick: (event) => event.stopPropagation(), onKeyDown: (event) => event.stopPropagation(), children: [
              /* @__PURE__ */ jsx5(Typography2, { sx: { fontSize: "0.75rem", fontWeight: 850 }, children: activeDriver.name }),
              /* @__PURE__ */ jsx5(Typography2, { sx: { mt: 0.15, fontSize: "0.66rem", color: chrome.textSecondary }, children: activeDriver.role })
            ] }),
            /* @__PURE__ */ jsxs4(Box3, { sx: { px: 1.5, py: 1.15, borderBottom: `1px solid ${chrome.border}` }, onClick: (event) => event.stopPropagation(), onKeyDown: (event) => event.stopPropagation(), children: [
              /* @__PURE__ */ jsx5(Typography2, { variant: "caption", sx: { display: "block", mb: 0.75, fontSize: "0.64rem", fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", color: chrome.textSecondary }, children: "Appearance" }),
              /* @__PURE__ */ jsx5(ThemeModeSelect, {})
            ] }),
            defaultAccountMenuItems.map((item) => /* @__PURE__ */ jsxs4(MenuItem, { onClick: () => handleAccountMenuSelect(item.id), sx: { minHeight: 34, fontSize: "0.75rem", gap: 1 }, children: [
              accountMenuIcon(item.id),
              item.label
            ] }, item.id)),
            onLogout ? [
              /* @__PURE__ */ jsx5(Divider2, { sx: { borderColor: chrome.border } }, "logout-divider"),
              /* @__PURE__ */ jsxs4(MenuItem, { onClick: handleLogoutClick, sx: { minHeight: 34, fontSize: "0.75rem", gap: 1, color: isDark ? "#FCA5A5" : "#DC2626" }, children: [
                /* @__PURE__ */ jsx5(LogoutOutlinedIcon, { sx: { fontSize: 16 } }),
                logoutLabel
              ] }, "logout")
            ] : null
          ]
        }
      ) : null,
      /* @__PURE__ */ jsx5(Box3, { component: "main", ref: mainContentRef, sx: { flex: 1, minHeight: 0, overflow: "auto", p: { xs: 1.5, md: 2 }, display: "flex", flexDirection: "column" }, children })
    ] })
  ] });
}

// src/components/layout/PageHeader.tsx
import { Box as Box4, Stack as Stack3, Typography as Typography3, Breadcrumbs, Link } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { jsx as jsx6, jsxs as jsxs5 } from "react/jsx-runtime";
function PageHeader({
  title,
  subtitle,
  icon,
  badges,
  breadcrumbs,
  actions,
  mb = 2.5,
  divider = true,
  sx
}) {
  return /* @__PURE__ */ jsxs5(
    Box4,
    {
      sx: {
        mb,
        mx: divider ? { xs: -1.5, md: -2 } : void 0,
        px: divider ? { xs: 1.5, md: 2 } : void 0,
        pb: divider ? 2 : void 0,
        borderBottom: divider ? "1px solid" : "none",
        borderColor: "divider",
        ...sx
      },
      children: [
        breadcrumbs && breadcrumbs.length > 0 && /* @__PURE__ */ jsx6(Breadcrumbs, { separator: /* @__PURE__ */ jsx6(NavigateNextIcon, { fontSize: "small" }), sx: { mb: 1 }, children: breadcrumbs.map(
          (crumb, idx) => idx === breadcrumbs.length - 1 ? /* @__PURE__ */ jsx6(Typography3, { variant: "caption", color: "text.primary", fontWeight: 600, children: crumb.label }, idx) : /* @__PURE__ */ jsx6(
            Link,
            {
              component: "button",
              variant: "caption",
              color: "text.secondary",
              underline: "hover",
              onClick: crumb.onClick,
              sx: { cursor: "pointer" },
              children: crumb.label
            },
            idx
          )
        ) }),
        /* @__PURE__ */ jsxs5(
          Box4,
          {
            sx: {
              display: "flex",
              alignItems: { xs: "stretch", sm: "flex-start" },
              justifyContent: "space-between",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" }
            },
            children: [
              /* @__PURE__ */ jsxs5(Box4, { sx: { minWidth: 0, flex: 1 }, children: [
                /* @__PURE__ */ jsxs5(
                  Stack3,
                  {
                    direction: "row",
                    spacing: 1.1,
                    sx: { alignItems: "center", flexWrap: "wrap", mb: subtitle ? 0.5 : 0, rowGap: 0.75 },
                    children: [
                      icon ? /* @__PURE__ */ jsx6(
                        Box4,
                        {
                          sx: {
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "primary.main",
                            "& .MuiSvgIcon-root": { fontSize: 24 }
                          },
                          children: icon
                        }
                      ) : null,
                      /* @__PURE__ */ jsx6(Typography3, { variant: "h5", fontWeight: 800, sx: { lineHeight: 1.2 }, children: title }),
                      badges
                    ]
                  }
                ),
                subtitle ? /* @__PURE__ */ jsx6(Typography3, { variant: "body2", color: "text.secondary", sx: { lineHeight: 1.45 }, children: subtitle }) : null
              ] }),
              actions ? /* @__PURE__ */ jsx6(
                Box4,
                {
                  sx: {
                    display: "flex",
                    gap: 1,
                    flexWrap: "wrap",
                    alignItems: { sm: "center" },
                    flex: 1,
                    minWidth: 0,
                    justifyContent: "flex-end"
                  },
                  children: actions
                }
              ) : null
            ]
          }
        )
      ]
    }
  );
}

// src/components/data/StatCard.tsx
import {
  Card as MuiCard,
  CardContent,
  CardHeader,
  Typography as Typography4,
  Box as Box5
} from "@mui/material";
import { jsx as jsx7, jsxs as jsxs6 } from "react/jsx-runtime";
function StatCard({ title, value, subtitle, icon, trend, accentColor, sx }) {
  const trendPositive = trend && trend.value >= 0;
  return /* @__PURE__ */ jsx7(MuiCard, { sx: { height: "100%", ...sx }, children: /* @__PURE__ */ jsxs6(CardContent, { sx: { p: 3, "&:last-child": { pb: 3 } }, children: [
    /* @__PURE__ */ jsxs6(Box5, { sx: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2 }, children: [
      /* @__PURE__ */ jsx7(Typography4, { variant: "overline", color: "text.secondary", children: title }),
      icon && /* @__PURE__ */ jsx7(
        Box5,
        {
          sx: {
            p: 1,
            borderRadius: 2,
            backgroundColor: accentColor ? `${accentColor}22` : "rgba(76,175,80,0.12)",
            color: accentColor ?? colors.primary[400],
            display: "flex",
            alignItems: "center"
          },
          children: icon
        }
      )
    ] }),
    /* @__PURE__ */ jsx7(Typography4, { variant: "h4", fontWeight: "bold", sx: { mb: 0.5 }, children: value }),
    subtitle && /* @__PURE__ */ jsx7(Typography4, { variant: "body2", color: "text.secondary", children: subtitle }),
    trend && /* @__PURE__ */ jsxs6(Box5, { sx: { display: "flex", alignItems: "center", mt: 1, gap: 0.5 }, children: [
      /* @__PURE__ */ jsxs6(
        Typography4,
        {
          variant: "caption",
          sx: { color: trendPositive ? colors.success.light : colors.error.light, fontWeight: 600 },
          children: [
            trendPositive ? "+" : "",
            trend.value
          ]
        }
      ),
      /* @__PURE__ */ jsx7(Typography4, { variant: "caption", color: "text.secondary", children: trend.label })
    ] })
  ] }) });
}
function SectionCard({ title, subtitle, action, children, sx, contentSx, noPadding }) {
  return /* @__PURE__ */ jsxs6(MuiCard, { sx, children: [
    (title || action) && /* @__PURE__ */ jsx7(
      CardHeader,
      {
        title: title && /* @__PURE__ */ jsx7(Typography4, { variant: "h6", children: title }),
        subheader: subtitle && /* @__PURE__ */ jsx7(Typography4, { variant: "body2", color: "text.secondary", children: subtitle }),
        action,
        sx: {
          pb: 0,
          px: 3,
          pt: 2.5,
          gap: 1.5,
          alignItems: "center",
          flexWrap: "wrap",
          "& .MuiCardHeader-content": { flex: "1 1 220px", minWidth: 0 },
          "& .MuiCardHeader-action": { flex: "0 1 auto", m: 0, maxWidth: "100%", minWidth: 0 }
        }
      }
    ),
    /* @__PURE__ */ jsx7(CardContent, { sx: { p: noPadding ? 0 : 3, "&:last-child": { pb: noPadding ? 0 : 3 }, ...contentSx }, children })
  ] });
}

// src/components/data/RacingWidgets.tsx
import { Box as Box6, LinearProgress, Tooltip as Tooltip2, Typography as Typography5 } from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { jsx as jsx8, jsxs as jsxs7 } from "react/jsx-runtime";
function LapDelta({ deltaMs, referenceLabel }) {
  const positive = deltaMs > 0;
  const label = `${positive ? "+" : ""}${(deltaMs / 1e3).toFixed(3)}`;
  const color = positive ? colors.error.light : colors.success.light;
  return /* @__PURE__ */ jsxs7(Box6, { sx: { display: "flex", alignItems: "center", gap: 0.5 }, children: [
    positive ? /* @__PURE__ */ jsx8(TrendingUpIcon, { sx: { fontSize: 16, color } }) : /* @__PURE__ */ jsx8(TrendingDownIcon, { sx: { fontSize: 16, color } }),
    /* @__PURE__ */ jsx8(
      Typography5,
      {
        variant: "body2",
        sx: { color, fontFamily: '"JetBrains Mono", monospace', fontWeight: 600 },
        children: label
      }
    ),
    referenceLabel && /* @__PURE__ */ jsxs7(Typography5, { variant: "caption", color: "text.secondary", children: [
      "vs ",
      referenceLabel
    ] })
  ] });
}
function ProgressBar({ value, label, color = colors.primary[500], showValue = true }) {
  return /* @__PURE__ */ jsxs7(Box6, { children: [
    (label || showValue) && /* @__PURE__ */ jsxs7(Box6, { sx: { display: "flex", justifyContent: "space-between", mb: 0.5 }, children: [
      label && /* @__PURE__ */ jsx8(Typography5, { variant: "caption", color: "text.secondary", children: label }),
      showValue && /* @__PURE__ */ jsxs7(Typography5, { variant: "caption", fontWeight: 600, children: [
        Math.round(value),
        "%"
      ] })
    ] }),
    /* @__PURE__ */ jsx8(
      LinearProgress,
      {
        variant: "determinate",
        value: Math.min(100, Math.max(0, value)),
        sx: {
          height: 6,
          backgroundColor: `${color}22`,
          "& .MuiLinearProgress-bar": { backgroundColor: color }
        }
      }
    )
  ] });
}
function AchievementBadge({ title, description, earned = false, earnedAt }) {
  return /* @__PURE__ */ jsx8(Tooltip2, { title: `${description}${earnedAt ? ` \xB7 ${earnedAt}` : ""}`, children: /* @__PURE__ */ jsxs7(
    Box6,
    {
      sx: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.5,
        p: 1.5,
        borderRadius: 2,
        backgroundColor: earned ? `${colors.secondary[700]}22` : "rgba(255,255,255,0.03)",
        border: `1px solid ${earned ? colors.secondary[700] : "rgba(255,255,255,0.06)"}`,
        filter: earned ? "none" : "grayscale(1)",
        opacity: earned ? 1 : 0.5,
        cursor: "default",
        width: 80,
        transition: "all 0.2s"
      },
      children: [
        /* @__PURE__ */ jsx8(
          EmojiEventsIcon,
          {
            sx: {
              fontSize: 28,
              color: earned ? colors.secondary[400] : colors.grey[600]
            }
          }
        ),
        /* @__PURE__ */ jsx8(
          Typography5,
          {
            variant: "caption",
            textAlign: "center",
            fontWeight: 600,
            sx: { lineHeight: 1.2, fontSize: "0.65rem" },
            children: title
          }
        )
      ]
    }
  ) });
}

// src/components/feedback/StatusBadge.tsx
import { Chip } from "@mui/material";
import { jsx as jsx9, jsxs as jsxs8 } from "react/jsx-runtime";
var STATUS_LABELS = {
  active: "Active",
  injured: "Injured",
  inactive: "Inactive",
  inUse: "In Use",
  available: "Available",
  maintenance: "Maintenance",
  inShop: "In Shop",
  overdue: "Overdue",
  due5days: "Due in 5 days",
  due7days: "Due in 7 days",
  race: "Race",
  practice: "Practice",
  test: "Test",
  qualifying: "Qualifying",
  positive: "Positive",
  neutral: "Neutral",
  needsWork: "Needs Work"
};
function StatusBadge({ status, customLabel, sx, size = "small", ...rest }) {
  const cfg = colors.status[status];
  return /* @__PURE__ */ jsx9(
    Chip,
    {
      label: customLabel ?? STATUS_LABELS[status],
      size,
      sx: {
        backgroundColor: cfg.bg,
        color: cfg.text,
        border: `1px solid ${cfg.border}`,
        fontWeight: 600,
        fontSize: "0.7rem",
        height: 22,
        borderRadius: "6px",
        "& .MuiChip-label": { px: 1 },
        ...sx
      },
      ...rest
    }
  );
}
function LapTimeBadge({ ms, isPersonalBest, isSessionBest }) {
  const seconds = (ms / 1e3).toFixed(3);
  const color = isPersonalBest ? colors.primary[400] : isSessionBest ? colors.primary[300] : "inherit";
  return /* @__PURE__ */ jsx9(
    "span",
    {
      style: {
        fontFamily: '"JetBrains Mono", "Fira Code", monospace',
        fontWeight: 600,
        fontSize: "0.85rem",
        color,
        letterSpacing: "0.02em"
      },
      children: seconds
    }
  );
}
function DriverRatingBadge({ rating, trend, size = "medium" }) {
  const sizePx = size === "small" ? 32 : size === "large" ? 52 : 40;
  const fontSize = size === "small" ? "0.75rem" : size === "large" ? "1rem" : "0.875rem";
  return /* @__PURE__ */ jsxs8(
    "span",
    {
      style: {
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center"
      },
      children: [
        /* @__PURE__ */ jsx9(
          "span",
          {
            style: {
              width: sizePx,
              height: sizePx,
              borderRadius: "50%",
              border: `2px solid ${colors.primary[500]}`,
              background: `${colors.primary[500]}22`,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: colors.primary[400],
              fontWeight: 700,
              fontSize,
              fontFamily: '"Inter", sans-serif'
            },
            children: rating
          }
        ),
        trend !== void 0 && /* @__PURE__ */ jsxs8(
          "span",
          {
            style: {
              fontSize: "0.65rem",
              fontWeight: 600,
              color: trend >= 0 ? colors.success.light : colors.error.light,
              marginTop: 2
            },
            children: [
              trend >= 0 ? "+" : "",
              trend
            ]
          }
        )
      ]
    }
  );
}

// src/components/feedback/StateViews.tsx
import {
  Box as Box7,
  CircularProgress,
  Typography as Typography6,
  Alert,
  AlertTitle
} from "@mui/material";
import SpeedIcon from "@mui/icons-material/Speed";
import { jsx as jsx10, jsxs as jsxs9 } from "react/jsx-runtime";
function LoadingScreen({ message = "Loading\u2026" }) {
  return /* @__PURE__ */ jsxs9(
    Box7,
    {
      sx: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
        gap: 2
      },
      children: [
        /* @__PURE__ */ jsx10(CircularProgress, { color: "primary" }),
        /* @__PURE__ */ jsx10(Typography6, { variant: "body2", color: "text.secondary", children: message })
      ]
    }
  );
}
function EmptyState({
  title,
  description,
  icon,
  action
}) {
  return /* @__PURE__ */ jsxs9(
    Box7,
    {
      sx: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 8,
        gap: 2,
        textAlign: "center"
      },
      children: [
        /* @__PURE__ */ jsx10(Box7, { sx: { opacity: 0.3, fontSize: 64 }, children: icon ?? /* @__PURE__ */ jsx10(SpeedIcon, { sx: { fontSize: 64 } }) }),
        /* @__PURE__ */ jsxs9(Box7, { children: [
          /* @__PURE__ */ jsx10(Typography6, { variant: "h6", gutterBottom: true, children: title }),
          description && /* @__PURE__ */ jsx10(Typography6, { variant: "body2", color: "text.secondary", sx: { maxWidth: 360 }, children: description })
        ] }),
        action
      ]
    }
  );
}
function ErrorBanner({ message, title = "Something went wrong" }) {
  return /* @__PURE__ */ jsxs9(Alert, { severity: "error", sx: { borderRadius: 2 }, children: [
    /* @__PURE__ */ jsx10(AlertTitle, { children: title }),
    message
  ] });
}

// src/index.ts
import {
  Box as Box8,
  Button as Button3,
  Card,
  CardContent as CardContent2,
  CardHeader as CardHeader2,
  Checkbox,
  Chip as Chip2,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider as Divider3,
  Grid,
  IconButton as IconButton3,
  InputAdornment,
  Link as Link2,
  List as List2,
  ListItem,
  ListItemIcon as ListItemIcon2,
  ListItemText as ListItemText2,
  MenuItem as MenuItem2,
  Paper as Paper2,
  Select,
  Stack as Stack4,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip as Tooltip3,
  Typography as Typography7,
  Avatar as Avatar2,
  Badge as Badge2,
  Alert as Alert2,
  AlertTitle as AlertTitle2,
  Autocomplete,
  FormControl,
  FormHelperText,
  InputLabel,
  Switch as Switch2,
  FormControlLabel,
  CircularProgress as CircularProgress2,
  LinearProgress as LinearProgress2,
  Skeleton,
  Snackbar
} from "@mui/material";
import {
  Dashboard,
  Speed,
  DirectionsCar,
  Person,
  Group,
  Build,
  EmojiEvents,
  CalendarToday,
  Timeline,
  Analytics,
  Settings,
  Logout,
  Add,
  Edit,
  Delete,
  Search,
  FilterList,
  Download,
  Upload,
  QrCode,
  Nfc,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  Cancel,
  Warning,
  Info,
  Star,
  FitnessCenter,
  LocalGasStation,
  AccessTime,
  Flag,
  Notifications,
  Message,
  PhotoCamera,
  Map
} from "@mui/icons-material";

// src/PrivacyPolicyContent.tsx
import { jsx as jsx11, jsxs as jsxs10 } from "react/jsx-runtime";
var PRIVACY_POLICY_EFFECTIVE_DATE = "October 4, 2026";
var SUPPORT_EMAIL = "support@paddiq.app";
function PrivacyPolicyContent() {
  return /* @__PURE__ */ jsxs10("article", { className: "policy-content", children: [
    /* @__PURE__ */ jsxs10("section", { id: "email-choices", children: [
      /* @__PURE__ */ jsx11("h2", { children: "Email communications and your choices" }),
      /* @__PURE__ */ jsx11("p", { children: "Marketing emails, including product news, tips, and offers, are optional. We ask you to choose through Email preferences in your account settings. Accounts with no recorded email preference may receive marketing until they opt out. A missing preference is not recorded as consent, and we honor existing opt-outs. We ask people who explicitly opt in to confirm they are 18 or older and are subscribing their own email address." }),
      /* @__PURE__ */ jsx11("p", { children: "We record your preference, the email address it applies to, the time and source of your choice, and the wording you accepted. We use our email delivery provider, Resend, to send messages and retain delivery records to operate and troubleshoot communications. Campaign audiences exclude deleted and disabled accounts and may be limited to accounts with activity in the last 90 days." }),
      /* @__PURE__ */ jsxs10("p", { children: [
        "You can stop marketing and administrator outreach using the unsubscribe link in campaign emails or Email preferences in account settings. You do not need to sign in to use an email unsubscribe link. We check opt-outs before handing new outreach messages to our delivery provider. A message already handed to the provider may still arrive. You can also contact ",
        /* @__PURE__ */ jsx11("a", { href: `mailto:${SUPPORT_EMAIL}`, children: SUPPORT_EMAIL }),
        "."
      ] }),
      /* @__PURE__ */ jsx11("p", { children: "Opting out does not stop password resets, email verification, receipts, security notices, or other essential account messages. We do not include promotions in those messages. We retain a restricted suppression record derived from your email address to honor your opt-out, including after account deletion. Marketing resumes only if you explicitly subscribe again. Administrators cannot subscribe you on your behalf." })
    ] }),
    /* @__PURE__ */ jsxs10("section", { children: [
      /* @__PURE__ */ jsx11("h2", { children: "1. Who we are" }),
      /* @__PURE__ */ jsx11("p", { children: 'Paddiq is a motorsports driver-development and race-operations platform for drivers, families, coaches, teams, and mechanics. Paddiq is launching with a focus on youth karting, with the broader goal of supporting drivers and racing programs across motorsports. In this policy, "Paddiq," "we," "us," and "our" refer to Paddiq LLC and the Paddiq services, including our website, web app, mobile app, support channels, and related services.' })
    ] }),
    /* @__PURE__ */ jsxs10("section", { children: [
      /* @__PURE__ */ jsx11("h2", { children: "2. Information we collect" }),
      /* @__PURE__ */ jsx11("p", { children: "We collect information you provide, information created through your use of Paddiq, and limited information received from the services you choose to connect." }),
      /* @__PURE__ */ jsxs10("ul", { children: [
        /* @__PURE__ */ jsxs10("li", { children: [
          /* @__PURE__ */ jsx11("strong", { children: "Account information:" }),
          " name, email address, account identifier, role, team or family membership, profile photo, and account settings."
        ] }),
        /* @__PURE__ */ jsxs10("li", { children: [
          /* @__PURE__ */ jsx11("strong", { children: "Sign-in information:" }),
          " if you use Sign in with Apple or Google, we receive the provider account identifier and the name or email address the provider makes available. We do not receive your Apple or Google password."
        ] }),
        /* @__PURE__ */ jsxs10("li", { children: [
          /* @__PURE__ */ jsx11("strong", { children: "Team, family, and driver information:" }),
          " team names, roster details, driver profiles, parent or guardian links, staff assignments, schedules, coaching context, and related race-weekend records."
        ] }),
        /* @__PURE__ */ jsxs10("li", { children: [
          /* @__PURE__ */ jsx11("strong", { children: "Kart and garage information:" }),
          " kart details, assignments, setup history, maintenance records, notes, images, and other information entered by authorized users."
        ] }),
        /* @__PURE__ */ jsxs10("li", { children: [
          /* @__PURE__ */ jsx11("strong", { children: "Telemetry and session information:" }),
          " uploaded MyChron or compatible data files, lap and channel data, GPS coordinates recorded during a session, circuit information, timestamps, session metadata, comparisons, notes, and analysis generated in Paddiq."
        ] }),
        /* @__PURE__ */ jsxs10("li", { children: [
          /* @__PURE__ */ jsx11("strong", { children: "Billing information:" }),
          " plan, subscription state, invoices, payment status, billing contact information, and payment processor references. We do not store full credit card numbers on our servers."
        ] }),
        /* @__PURE__ */ jsxs10("li", { children: [
          /* @__PURE__ */ jsx11("strong", { children: "Support information:" }),
          " your name, email address, messages, attachments, troubleshooting details, and communications with us."
        ] }),
        /* @__PURE__ */ jsxs10("li", { children: [
          /* @__PURE__ */ jsx11("strong", { children: "Device and service information:" }),
          " browser or device type, operating system, IP address, app version, request timestamps, security events, error logs, and diagnostics needed to operate and protect the service."
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsxs10("section", { children: [
      /* @__PURE__ */ jsx11("h2", { children: "3. Youth drivers and parent or guardian controls" }),
      /* @__PURE__ */ jsx11("p", { children: "Because Paddiq currently serves youth karting families and teams, some driver profiles and telemetry may relate to children. A parent, legal guardian, or team representative authorized by the parent or guardian should create or manage a child driver profile and decide who may access it. Team staff must only add or use a child's information when they have appropriate authority to do so." }),
      /* @__PURE__ */ jsx11("p", { children: "We use youth-driver information to provide driver profiles, session review, telemetry comparison, setup history, team collaboration, and driver-development features. We do not sell children's personal information or use it for targeted advertising." }),
      /* @__PURE__ */ jsxs10("p", { children: [
        "A parent or guardian may ask to review, correct, export, or delete a child's information, or withdraw permission for its continued use, by contacting",
        " ",
        /* @__PURE__ */ jsx11("a", { href: `mailto:${SUPPORT_EMAIL}`, children: SUPPORT_EMAIL }),
        ". We may need to verify the requester's identity, relationship to the child, and authority over the relevant team or family workspace. If you believe a child provided personal information without appropriate parent or guardian authorization, please contact us."
      ] })
    ] }),
    /* @__PURE__ */ jsxs10("section", { children: [
      /* @__PURE__ */ jsx11("h2", { children: "4. Mobile app permissions and local data" }),
      /* @__PURE__ */ jsxs10("ul", { children: [
        /* @__PURE__ */ jsxs10("li", { children: [
          /* @__PURE__ */ jsx11("strong", { children: "Local network:" }),
          " when you choose to connect to a MyChron device over local Wi-Fi, the iOS app accesses the local network to list or download sessions from that device. This connection is initiated by you."
        ] }),
        /* @__PURE__ */ jsxs10("li", { children: [
          /* @__PURE__ */ jsx11("strong", { children: "Files and photos:" }),
          " when you choose an import or profile-image feature, the app opens the system file or photo picker. Paddiq receives only the item you select, not your entire library."
        ] }),
        /* @__PURE__ */ jsxs10("li", { children: [
          /* @__PURE__ */ jsx11("strong", { children: "Data stored on your device:" }),
          " the app may temporarily keep downloaded sessions, upload state, saved MyChron connection details, authentication state, and preferences so features work reliably. Removing the app generally removes app-local data, but it does not delete information already uploaded to your Paddiq account."
        ] })
      ] }),
      /* @__PURE__ */ jsx11("p", { children: "Paddiq does not request continuous background location access. Location coordinates shown in telemetry come from the racing data file or logger." })
    ] }),
    /* @__PURE__ */ jsxs10("section", { children: [
      /* @__PURE__ */ jsx11("h2", { children: "5. How we use information" }),
      /* @__PURE__ */ jsxs10("ul", { children: [
        /* @__PURE__ */ jsx11("li", { children: "Provide, maintain, secure, and improve Paddiq." }),
        /* @__PURE__ */ jsx11("li", { children: "Create and manage accounts, driver profiles, teams, roles, parent or guardian links, subscriptions, and support requests." }),
        /* @__PURE__ */ jsx11("li", { children: "Import and process telemetry, calculate lap and corner comparisons, and connect sessions with drivers, karts, circuits, and setups." }),
        /* @__PURE__ */ jsx11("li", { children: "Provide garage, maintenance, scheduling, collaboration, and driver-development workflows." }),
        /* @__PURE__ */ jsx11("li", { children: "Send account verification, team invitations, service messages, support replies, billing notices, and security alerts." }),
        /* @__PURE__ */ jsx11("li", { children: "Debug issues, prevent abuse, protect accounts, and comply with legal obligations." }),
        /* @__PURE__ */ jsx11("li", { children: "Review aggregated or de-identified service information to understand reliability and improve features." })
      ] })
    ] }),
    /* @__PURE__ */ jsxs10("section", { children: [
      /* @__PURE__ */ jsx11("h2", { children: "6. How information is visible and shared" }),
      /* @__PURE__ */ jsx11("p", { children: "We share information only as needed to operate Paddiq, comply with law, protect rights, or with your direction." }),
      /* @__PURE__ */ jsxs10("ul", { children: [
        /* @__PURE__ */ jsxs10("li", { children: [
          /* @__PURE__ */ jsx11("strong", { children: "Within teams and family workspaces:" }),
          " driver, kart, session, setup, schedule, and related information may be visible to team owners, authorized staff, parents, guardians, and drivers according to their roles and permissions."
        ] }),
        /* @__PURE__ */ jsxs10("li", { children: [
          /* @__PURE__ */ jsx11("strong", { children: "At your direction:" }),
          " information may move between family and team workspaces when an authorized user links, transfers, or shares a driver or related record."
        ] }),
        /* @__PURE__ */ jsxs10("li", { children: [
          /* @__PURE__ */ jsx11("strong", { children: "Service providers:" }),
          " Google Firebase and Google Cloud provide authentication, database, storage, and hosting infrastructure; Apple and Google support optional sign-in; Stripe processes subscriptions and payments; and Resend supports service email. These providers process information for us under their terms and applicable contractual restrictions."
        ] }),
        /* @__PURE__ */ jsxs10("li", { children: [
          /* @__PURE__ */ jsx11("strong", { children: "Legal and safety:" }),
          " we may disclose information if required by law or if necessary to protect Paddiq, our users, or others."
        ] }),
        /* @__PURE__ */ jsxs10("li", { children: [
          /* @__PURE__ */ jsx11("strong", { children: "Business transfers:" }),
          " information may be transferred as part of a merger, acquisition, financing, or sale of assets, subject to this policy or a policy with similar protections."
        ] })
      ] }),
      /* @__PURE__ */ jsx11("p", { children: "We do not sell personal information, rent user lists, or share personal information for third-party targeted advertising." })
    ] }),
    /* @__PURE__ */ jsxs10("section", { children: [
      /* @__PURE__ */ jsx11("h2", { children: "7. Payments" }),
      /* @__PURE__ */ jsx11("p", { children: "Payments and subscriptions are processed by Stripe. Stripe may collect payment card details and billing information directly under its own terms and privacy policy. Paddiq receives limited billing records such as customer identifiers, subscription status, invoice status, and plan information." })
    ] }),
    /* @__PURE__ */ jsxs10("section", { children: [
      /* @__PURE__ */ jsx11("h2", { children: "8. Cookies and similar technologies" }),
      /* @__PURE__ */ jsx11("p", { children: "We and our providers may use cookies, local storage, and similar technologies to keep you signed in, remember preferences, secure sessions, measure usage, and troubleshoot the service. Browser settings may let you limit cookies, but some features may not work correctly without them." })
    ] }),
    /* @__PURE__ */ jsxs10("section", { children: [
      /* @__PURE__ */ jsx11("h2", { children: "9. Data retention and deletion" }),
      /* @__PURE__ */ jsx11("p", { children: "We keep information for as long as needed to provide Paddiq, comply with legal obligations, resolve disputes, enforce agreements, and maintain security. Retention periods may vary by plan, data type, workspace settings, and legal requirements. Account holders can request account deletion through the app or contact us. Parents and guardians may also request deletion of a child's information as described above." }),
      /* @__PURE__ */ jsx11("p", { children: "Deleting an account may not immediately remove records controlled by a team or another authorized account, information another user is legally entitled to retain, or records we must keep for billing, fraud prevention, safety, dispute resolution, or legal compliance. Deleted information may remain in encrypted backups and system logs for a limited period before being overwritten." })
    ] }),
    /* @__PURE__ */ jsxs10("section", { children: [
      /* @__PURE__ */ jsx11("h2", { children: "10. Security" }),
      /* @__PURE__ */ jsx11("p", { children: "We use administrative, technical, and organizational safeguards designed to protect personal information. No system is perfectly secure, so we cannot guarantee that information will never be accessed, disclosed, altered, or destroyed without authorization." })
    ] }),
    /* @__PURE__ */ jsxs10("section", { children: [
      /* @__PURE__ */ jsx11("h2", { children: "11. Your choices and privacy rights" }),
      /* @__PURE__ */ jsxs10("p", { children: [
        "Depending on where you live, you may have rights to access, correct, delete, restrict, object to, or receive a copy of certain personal information and to withdraw consent where processing is based on consent. You can update some information and request account deletion inside the app. For other requests, contact us at",
        " ",
        /* @__PURE__ */ jsx11("a", { href: `mailto:${SUPPORT_EMAIL}`, children: SUPPORT_EMAIL }),
        ". We may verify your identity and authority before completing a request. You may also have the right to appeal our response or complain to your local privacy regulator."
      ] })
    ] }),
    /* @__PURE__ */ jsxs10("section", { children: [
      /* @__PURE__ */ jsx11("h2", { children: "12. International data processing" }),
      /* @__PURE__ */ jsx11("p", { children: "Paddiq and our providers may process information in the United States and other countries. These countries may have privacy laws that differ from where you live. We take steps designed to protect information as described in this policy." })
    ] }),
    /* @__PURE__ */ jsxs10("section", { children: [
      /* @__PURE__ */ jsx11("h2", { children: "13. Third-party links and services" }),
      /* @__PURE__ */ jsx11("p", { children: "Paddiq may link to third-party websites, app stores, payment pages, or services. Their privacy practices are governed by their own policies. This policy covers Paddiq's handling of information, not a third party's independent practices." })
    ] }),
    /* @__PURE__ */ jsxs10("section", { children: [
      /* @__PURE__ */ jsx11("h2", { children: "14. Changes to this policy" }),
      /* @__PURE__ */ jsx11("p", { children: "We may update this policy from time to time. If we make material changes, we will update the effective date and may provide additional notice through the product, website, or email." })
    ] }),
    /* @__PURE__ */ jsxs10("section", { children: [
      /* @__PURE__ */ jsx11("h2", { children: "15. Contact us" }),
      /* @__PURE__ */ jsxs10("p", { children: [
        "Questions or privacy requests can be sent to",
        " ",
        /* @__PURE__ */ jsx11("a", { href: `mailto:${SUPPORT_EMAIL}`, children: SUPPORT_EMAIL }),
        "."
      ] })
    ] })
  ] });
}
export {
  AchievementBadge,
  Add as AddIcon,
  Alert2 as Alert,
  AlertTitle2 as AlertTitle,
  Analytics as AnalyticsIcon,
  AppShell,
  Autocomplete,
  Avatar2 as Avatar,
  Badge2 as Badge,
  Box8 as Box,
  Button3 as Button,
  CalendarToday as CalendarIcon,
  PhotoCamera as CameraIcon,
  Cancel as CancelIcon,
  Card,
  CardContent2 as CardContent,
  CardHeader2 as CardHeader,
  CheckCircle as CheckCircleIcon,
  Checkbox,
  Chip2 as Chip,
  CircularProgress2 as CircularProgress,
  Dashboard as DashboardIcon,
  Delete as DeleteIcon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider3 as Divider,
  Download as DownloadIcon,
  DriverRatingBadge,
  Edit as EditIcon,
  EmptyState,
  ErrorBanner,
  FilterList as FilterIcon,
  FitnessCenter as FitnessIcon,
  Flag as FlagIcon,
  FormControl,
  FormControlLabel,
  FormHelperText,
  LocalGasStation as FuelIcon,
  Grid,
  GridlineProvider,
  IconButton3 as IconButton,
  Info as InfoIcon,
  InputAdornment,
  InputLabel,
  InterfaceSettingsPanel,
  DirectionsCar as KartIcon,
  LapDelta,
  LapTimeBadge,
  LinearProgress2 as LinearProgress,
  Link2 as Link,
  List2 as List,
  ListItem,
  ListItemIcon2 as ListItemIcon,
  ListItemText2 as ListItemText,
  LoadingScreen,
  Logout as LogoutIcon,
  Build as MaintenanceIcon,
  Map as MapIcon,
  MenuItem2 as MenuItem,
  Message as MessageIcon,
  Nfc as NfcIcon,
  Notifications as NotificationsIcon,
  PRIVACY_POLICY_EFFECTIVE_DATE,
  PaddiqLogo,
  PaddiqProvider,
  PageHeader,
  Paper2 as Paper,
  Person as PersonIcon,
  PrivacyPolicyContent,
  ProgressBar,
  QrCode as QrCodeIcon,
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_WIDTH,
  Search as SearchIcon,
  SectionCard,
  Select,
  Settings as SettingsIcon,
  Skeleton,
  Snackbar,
  Speed as SpeedIcon,
  Stack4 as Stack,
  Star as StarIcon,
  StatCard,
  StatusBadge,
  Switch2 as Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Group as TeamIcon,
  TextField,
  ThemeModeSelect,
  Timeline as TimelineIcon,
  AccessTime as TimerIcon,
  Tooltip3 as Tooltip,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
  EmojiEvents as TrophyIcon,
  Typography7 as Typography,
  Upload as UploadIcon,
  Warning as WarningIcon,
  breakpoints,
  colors,
  darkTheme,
  footageCardSx,
  footageDrawerHeaderSx,
  footageDrawerPaperSx,
  footageTextFieldSx,
  getFootageChrome,
  getHeaderChrome,
  getLapMetaChrome,
  getSessionDrawerChrome,
  getSidebarChrome,
  getTraceChrome,
  headerOutlinedButtonSx,
  headerReplayBarSimpleSx,
  headerReplayPlayButtonSx,
  headerSectionLabelSx,
  headerWeatherChipSx,
  headerWeatherPanelSx,
  lapMetaDrawerHeaderSx,
  lapMetaDrawerPaperSx,
  lapMetaTableContainerSx,
  lapMetaToggleGroupSx,
  lightTheme,
  mapOverlayButtonSx,
  mapPlaybackDockLegendSx,
  mapPlaybackDockOuterSx,
  mapPlaybackDockReplaySx,
  mapPlaybackDockShellSx,
  sessionDrawerCardSx,
  sessionDrawerCollapsibleSectionSx,
  sessionDrawerCompareAddButtonSx,
  sessionDrawerHeaderSx,
  sessionDrawerIconButtonSx,
  sessionDrawerLapTableShellSx,
  sessionDrawerLapToneStyles,
  sessionDrawerPaperSx,
  sessionDrawerResetButtonSx,
  sessionDrawerSectionLabelSx,
  sessionDrawerSelectMenuButtonSx,
  sessionDrawerSelectMenuPaperSx,
  sessionDrawerSelectSx,
  sessionDrawerToggleGroupSx,
  shadows,
  shape,
  spacingUnit,
  theme,
  traceDrawerHeaderSx,
  traceDrawerShellSx,
  tracePanelSx,
  tracePickerDrawerPaperSx,
  typography,
  uploadChoiceButtonSx,
  uploadDialogPaperSx,
  uploadDialogSelectSx,
  useColorMode,
  useInterfacePreferences,
  zIndex
};
//# sourceMappingURL=index.mjs.map